// ** React imports
import {
  FC,
  forwardRef,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";

// ** MUI Imports
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InputBase from "@mui/material/InputBase";

// ** Icons imports
import ClockOutline from "mdi-material-ui/ClockOutline";
import CalendarMonthOutline from "mdi-material-ui/CalendarMonthOutline";

// ** Third Party Imports
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "moment/locale/fr";
import "moment/locale/nl";
import fr from "date-fns/locale/fr";
import nl from "date-fns/locale/nl";
import en from "date-fns/locale/en-US";
import { useTranslation } from "react-i18next";
import format from "date-fns/format";
import moment from "moment";

// ** Components imports
// ** Styles imports
import DatePickerWrapper from "../../styles/Datepicker";
import TimePickerWrapper from "../../styles/TimePickerWrapper";
import { useLocation } from "react-router-dom";

export const CustomDateInput: FC<any> = forwardRef((props, ref) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const startDate = props.start
    ? props.selectedLDR
      ? moment.utc(props.start)
      : moment(props.start)
    : moment();
  startDate.locale(currentLanguage);

  const formattedDate = startDate.format("D MMM YYYY");
  const value = `${formattedDate}`;

  // const startDate = props.start
  //   ? moment(props.start).locale(currentLanguage).format("D MMM YYYY")
  //   : "";

  // const value = `${startDate}`;
  return (
    <>
      <InputBase
        sx={{
          flex: 1,
          fontSize: "",
          cursor: "pointer",
          width: "100%",
        }}
        placeholder={t("searchbar")}
        size="small"
        inputRef={ref}
        {...props}
        value={value}
      />
    </>
  );
});

export const CustomTimeInput: FC<any> = forwardRef((props, ref) => {
  // ** translation
  const { t } = useTranslation();
  const startDate = moment(props.start);

  // ** Start time only at x0 or x0

  const formattedDate = startDate.format("HH:mm");

  const value = `${formattedDate}`;
  return (
    <>
      <InputBase
        sx={{
          flex: 1,
          fontSize: "",
          cursor: "pointer",
        }}
        placeholder={t("searchbar")}
        size="small"
        inputRef={ref}
        {...props}
        value={value}
      />
    </>
  );
});

/***key acount input time begin*/
export const CustomInputTime: FC<any> = forwardRef((props, ref) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const startDate = moment(props.start);

  // ** Start time only at x0 or x0
  const currentMinute = startDate.minute();

  const roundedMinute = Math.round(currentMinute / 5) * 5;
  const newMinute = roundedMinute;

  startDate.minute(newMinute);

  const timeFormat = "HH:mm";

  const formattedDate = startDate.format(timeFormat);

  const value = `${formattedDate}`;
  return (
    <>
      <InputBase
        sx={{
          // ml: 1,
          flex: 1,
          fontSize: "",
          cursor: "pointer",
        }}
        placeholder={t("searchbar")}
        size="small"
        inputRef={ref}
        {...props}
        value={value}
      />
    </>
  );
});
/*****end */
export const CustomInputRange: FC<any> = forwardRef((props, ref) => {
  const startDate = props.start ? format(props.start, "dd/MM/yyyy") : "";
  const endDate =
    props.end !== null ? ` - ${format(props.end, "dd/MM/yyyy")}` : null;
  const value = `${startDate}${endDate !== null ? endDate : ""}`;

  return (
    <InputBase
      sx={{
        ml: 1,
        fontSize: "",
        cursor: "pointer",
        width: "100%",
      }}
      placeholder=""
      size="small"
      inputRef={ref}
      {...props}
      value={value}
    />
  );
});

export const CustomDatePicker: FC<any> = ({
  title,
  date,
  minTime,
  getDate,
  minDate,
  maxDate,
  prevDate, // startDate
  bottomBorderOnly,
  borderRadius,
  disabled,
  selectedLDR,
  selectedPeriod,
  notifyTimeUpdate,
}) => {
  // ** Hooks
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );
  const [startDate, setDate] = useState<any>(date);
  const [selectedTime, setSelectedTime] = useState<any>(date);
  const [isEntry, setIsEntry] = useState<any>(true);
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [isTimeOpen, setIsTimeOpen] = useState<boolean>(false);

  // ** keep internal date/time in sync whenever the parent recomputes `date`
  // (e.g. the LDR end date shifting when the entry date or duration changes)
  useEffect(() => {
    if (
      date &&
      (!startDate || new Date(startDate).getTime() !== new Date(date).getTime())
    ) {
      setDate(date);
      setSelectedTime(date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    if (selectedLDR) {
      const currentMoment = moment();
      const roundedMinutes = Math.ceil(currentMoment.minutes() / 15) * 15;
      if (minDate && currentMoment.minutes(roundedMinutes).toDate() < minDate) {
        getDate(minDate);
        setDate(minDate);
      } else {
        getDate(currentMoment.minutes(roundedMinutes).toDate());
        setDate(currentMoment.minutes(roundedMinutes).toDate());
      }
    }
  }, [selectedPeriod, selectedLDR]);

  // **
  const handleDateChange = (selectedDate: any) => {
    setDate(selectedDate);
    setIsCalendarOpen(false);
  };

  const handleTimeChange = (time: any) => {
    // Adjust the selected time to the nearest 15-minute interval
    const roundedMinutes = Math.ceil(moment(time).minutes() / 15) * 15;
    const adjustedTime = moment(time).minutes(roundedMinutes);
    setSelectedTime(adjustedTime.toDate());
    setIsTimeOpen(false);
  };

  const combinedDate = new Date(startDate);
  combinedDate.setHours(selectedTime.getHours());
  combinedDate.setMinutes(selectedTime.getMinutes());

  useEffect(() => {
    setIsEntry(title?.toLowerCase() === "sortie" ? false : true);
  }, [title]);

  useEffect(() => {
    getDate(combinedDate);
    // eslint-disable-next-line
  }, [startDate, selectedTime]);

  // ** maxTime
  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();

  useEffect(() => {
    if (!isEntry) {
      const timeBetween = moment(date).diff(moment(prevDate), "minutes");
      const isSameDay = moment(date).isSame(moment(prevDate), "day");

      if (isSameDay && timeBetween < 60) {
        setSelectedTime(moment(prevDate).add(1, "hour").toDate());
      }
    }
    //eslint-disable-next-line
  }, [prevDate]);

  return (
    <Box
      sx={{
        flex: 1,
        borderBottom: bottomBorderOnly
          ? "1px solid #D9D9D9"
          : "1px solid #D9D9D9",
        borderLeft: bottomBorderOnly ? "none" : "1px solid #D9D9D9",
        borderRight: bottomBorderOnly ? "none" : "1px solid #D9D9D9",
        borderTop: bottomBorderOnly ? "none" : "1px solid #D9D9D9",
        borderRadius: borderRadius ? borderRadius : "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
        cursor: "pointer",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}
        onClick={() => !disabled && setIsCalendarOpen(true)}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <Box>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t(title)}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              locale={currentLanguage}
              minDate={
                selectedLDR
                  ? new Date() > minDate
                    ? new Date()
                    : minDate
                  : isEntry
                  ? new Date()
                  : minDate
              }
              maxDate={maxDate ? maxDate : null}
              selected={date}
              onChange={handleDateChange}
              disabled={disabled}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={date}
                  selectedLDR={selectedLDR}
                />
              }
              open={isCalendarOpen}
              onClickOutside={() => setIsCalendarOpen(false)}
            />
          </DatePickerWrapper>
        </Box>
      </Box>
      {!!!selectedLDR && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            p: 1,
            cursor: "pointer",
            border: notifyTimeUpdate ? "1px solid red" : "none",
            borderTopRightRadius: !notifyTimeUpdate ? "none" : "8px",
            boxShadow: !notifyTimeUpdate ? "none" : "0 0 8px 2px red",
            borderBottomRightRadius: !notifyTimeUpdate ? "none" : "8px",
            transition: !notifyTimeUpdate
              ? "none"
              : "box-shadow 0.3s ease-in-out, border 0.3s ease-in-out",
          }}
          onClick={() => !disabled && setIsTimeOpen(!isTimeOpen)}>
          <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
          <Box>
            <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
              {t("Heure")}
            </Typography>
            <TimePickerWrapper>
              <DatePicker
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                dateFormat="HH:mm"
                timeFormat="HH:mm"
                selected={selectedTime}
                onChange={handleTimeChange}
                minTime={minTime}
                maxTime={endOfDayAsDate}
                customInput={
                  <CustomTimeInput
                    label={t("Sélectionner une date")}
                    start={selectedTime}
                  />
                }
                open={isTimeOpen}
                disabled={disabled}
                onClickOutside={() => setIsTimeOpen(false)}
              />
            </TimePickerWrapper>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const DatePickerBox: FC<any> = ({
  title,
  getStartDate,
  start,
  // endDate
}) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [startDate, setEndDate] = useState<any>(new Date());
  const [selectedTime, setSelectedTime] = useState<any>(new Date());
  const [minTimeSortie, setMinTimeSortie] = useState<any>();

  const handleTimeChange = (time: any) => {
    setSelectedTime(time);
  };

  useEffect(() => {
    getStartDate(startDate, selectedTime);
    // eslint-disable-next-line
  }, [startDate, selectedTime]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  const minTime = moment().toDate();
  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();

  const endOfDaySortie = moment(new Date()).endOf("day");
  const maxTimeSortie =
    endOfDaySortie.diff(startDate, "minutes") < 30
      ? startDate
      : endOfDaySortie.toDate();

  useEffect(() => {
    const now = moment();
    const startTime = moment(start, "YYYY-MM-DDTHH:mm:ss");
    const diffInMinutes = startTime.diff(now, "minutes");
    if (diffInMinutes < 30) {
    }

    const minTimeSortie = moment()
      .year(startTime.year())
      .month(startTime.month())
      .date(startTime.date())
      .hour(startTime.hour())
      .minute(startTime.minute())
      .add(30, "minutes")
      .toDate();

    setMinTimeSortie(minTimeSortie);
  }, [start]);
  return (
    <Box
      sx={{
        flex: 1,
        borderLeft: "1px solid #D9D9D9",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
          flex: 1,
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <Box component="div">
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {title}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              locale={currentLanguage}
              minDate={new Date()}
              selected={startDate}
              onChange={(date: any) => setEndDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={startDate}
                />
              }
            />
          </DatePickerWrapper>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          flex: 1,
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <Box component="div">
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            {/* {title === t("Date d'entrée") ? (
             */}
            {title === t("Date d'entrée") ? (
              <DatePicker
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                dateFormat="HH:mm"
                timeFormat="HH:mm"
                selected={selectedTime}
                onChange={handleTimeChange}
                minTime={minTime}
                maxTime={endOfDayAsDate} // sets maximum time to end of the day
                customInput={
                  <CustomTimeInput
                    label={t("Sélectionner une date")}
                    start={selectedTime}
                  />
                }
              />
            ) : (
              <DatePicker
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                dateFormat="HH:mm"
                timeFormat="HH:mm"
                selected={selectedTime}
                onChange={handleTimeChange}
                minTime={minTimeSortie}
                maxTime={maxTimeSortie} // sets maximum time to end of the day
                customInput={
                  <CustomTimeInput
                    label={t("Sélectionner une date")}
                    start={selectedTime}
                  />
                }
              />
            )}
          </TimePickerWrapper>
        </Box>
      </Box>
    </Box>
  );
};

export const EntryDatePickerResponsive: FC<any> = ({ getStartDate }) => {
  // ** Hooks
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const prevStartDate = location?.state?.startDate;
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );
  const [startDate, setStartDate] = useState<any>(new Date());
  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  const minTime = moment().toDate();
  if (moment(startDate).isSame(moment(), "day")) {
    minTime.setHours(moment().hours());
    minTime.setMinutes(Math.ceil(moment().minutes() / 15) * 15);
  } else {
    minTime.setHours(0);
    minTime.setMinutes(0);
  }

  const [selectedTime, setSelectedTime] = useState<any>(minTime);

  useEffect(() => {
    if (prevStartDate) {
      setStartDate(prevStartDate);
      setSelectedTime(prevStartDate);
    }
  }, []);
  // **

  const handleTimeChange = (time: any) => {
    // Adjust the selected time to the nearest 15-minute interval
    const roundedMinutes = Math.ceil(moment(time).minutes() / 15) * 15;
    const adjustedTime = moment(time).minutes(roundedMinutes);

    // Set the selected time state
    setSelectedTime(adjustedTime.toDate());
  };
  const combinedDate = new Date(startDate);

  combinedDate.setHours(selectedTime.getHours());
  combinedDate.setMinutes(selectedTime.getMinutes());

  useEffect(() => {
    getStartDate(combinedDate);
    // eslint-disable-next-line
  }, [startDate, selectedTime]);

  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();
  return (
    <Box
      sx={{
        flex: 1,
        borderBottom: "2px solid #D9D9D9",
        display: "flex",
        color: "#8A8A8A",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Entrée")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              locale={currentLanguage}
              minDate={new Date()}
              selected={startDate}
              onChange={(date: any) => setStartDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={startDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>
      <Box sx={{ borderRight: "2px solid #D9D9D9", mb: 1 }}></Box>
      <Box
        sx={{
          p: 1,
          flex: 1,
          display: "flex",
          cursor: "pointer",
          alignItems: "center",
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            <DatePicker
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="HH:mm"
              timeFormat="HH:mm"
              selected={selectedTime}
              onChange={handleTimeChange}
              minTime={minTime}
              maxTime={endOfDayAsDate} // sets maximum time to end of the day
              customInput={
                <CustomTimeInput
                  label={t("Sélectionner une date")}
                  start={selectedTime}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const ExitDatePickerResponsive: FC<any> = ({
  getEndDate,
  startDate,
}) => {
  // ** Hooks
  const { t, i18n } = useTranslation();
  const location = useLocation();
  // const prevEndDate = location?.state?.endDate;
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [endDate, setEndDate] = useState<any>(new Date());
  const [minTime, setMinTime] = useState<any>(new Date());
  const [minDate, setMinDate] = useState<any>(new Date());
  const [prevEndDate, setPrevEndDate] = useState<any>(location?.state?.endDate);
  const [selectedTime, setSelectedTime] = useState<any>(minTime);

  useEffect(() => {
    if (prevEndDate) {
      setEndDate(prevEndDate);
      setSelectedTime(prevEndDate);
    }
  }, [prevEndDate]);

  // **

  const combinedDate = new Date(endDate);

  combinedDate.setHours(selectedTime.getHours());
  combinedDate.setMinutes(selectedTime.getMinutes());

  const handleTimeChange = (time: any) => {
    // Adjust the selected time to the nearest 15-minute interval
    // Set the selected time state
    setSelectedTime(time);
    setPrevEndDate(null);
  };

  const handleDateChange = (time: any) => {
    // Adjust the selected time to the nearest 15-minute interval
    // Set the selected time state
    setEndDate(time);
    setPrevEndDate(null);
  };
  useEffect(() => {
    getEndDate(combinedDate);
    // eslint-disable-next-line
  }, [endDate, selectedTime]);
  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();

  useEffect(() => {
    // ** Disable previous hours => end from current time until the end of the day (minTime/maxTime)
    const updatedMinTime = moment(startDate).add(1, "hour");
    const roundedMinutes = Math.ceil(updatedMinTime.minutes() / 15) * 15;
    updatedMinTime.minutes(roundedMinutes);
    // const isSameDay = moment(prevEndDate).isSame(moment(startDate), "day");
    if (!prevEndDate) {
      if (moment(endDate).isSame(moment(startDate), "day")) {
        setMinTime(updatedMinTime.toDate());
        if (moment(selectedTime).diff(moment(startDate), "minutes") < 60) {
          setSelectedTime(updatedMinTime.toDate());
        }
        // **
      }
      if (moment(endDate).isSame(moment(startDate), "day")) {
        setEndDate(startDate);
        const updatedMinTime = moment(startDate).add(1, "hour");
        const roundedMinutes = Math.ceil(updatedMinTime.minutes() / 15) * 15;
        updatedMinTime.minutes(roundedMinutes);
        setMinTime(updatedMinTime.toDate());
      } else if (moment(endDate).isAfter(moment(startDate), "day")) {
        setMinTime(new Date("00:00:00"));
      }
    } else {
      const roundedSelectedTime = moment(prevEndDate)
        .startOf("hour")
        .add(Math.ceil(moment(prevEndDate).minutes() / 15) * 15, "minutes")
        .toDate();
      if (moment(prevEndDate).diff(moment(startDate), "hours") > 1) {
        setEndDate(prevEndDate);
        setSelectedTime(roundedSelectedTime);
      } else {
        const updatedMinTime = moment(startDate).add(1, "hour");
        const roundedMinutes = Math.ceil(updatedMinTime.minutes() / 15) * 15;
        updatedMinTime.minutes(roundedMinutes);
      }

      setMinDate(startDate);
      // }
    }
  }, [startDate, endDate, prevEndDate]);
  // ** if endDate is bigger than the startDate
  useEffect(() => {
    const start = moment(startDate).format("YYYY-DD-MM HH:mm");
    const end = moment(endDate).format("YYYY-DD-MM HH:mm");

    if (!prevEndDate && start > end) {
      setEndDate(new Date(startDate));
      setMinDate(new Date(startDate));
    }
    const endOfDay = moment(moment(selectedTime).endOf("day")).diff(
      startDate,
      "minutes",
    );
    const isSameDay = moment(endDate).isSame(moment(startDate), "day");

    const updatedMinTime = moment(startDate).add(1, "hour");
    const roundedMinutes = Math.ceil(updatedMinTime.minutes() / 15) * 15;
    updatedMinTime.minutes(roundedMinutes);
    if (isSameDay && endOfDay < 60) {
      const addDay = moment(startDate).add(1, "days").toDate();
      setEndDate(addDay);
      setMinDate(addDay);
      setMinTime(moment(updatedMinTime).toDate());
      setSelectedTime(moment(updatedMinTime).toDate());
    } else {
      setMinDate(new Date(startDate));
    }
    // eslint-disable-next-line
  }, [startDate, prevEndDate]);

  return (
    <Box
      sx={{
        flex: 1,
        borderBottom: "2px solid #D9D9D9",
        display: "flex",
        color: "#8A8A8A",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Entrée")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              locale={currentLanguage}
              minDate={minDate}
              selected={endDate}
              onChange={handleDateChange}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={endDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>
      <Box sx={{ borderRight: "2px solid #D9D9D9", mb: 1 }}></Box>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            <DatePicker
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="HH:mm"
              timeFormat="HH:mm"
              selected={selectedTime}
              onChange={handleTimeChange}
              minTime={minTime}
              maxTime={endOfDayAsDate} // sets maximum time to end of the day
              customInput={
                <CustomTimeInput
                  label={t("Sélectionner une date")}
                  start={selectedTime}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};
export const CustomDatePickerNoBorder: FC<any> = ({
  title,
  date,
  minTime,
  getDate,
  minDate,
  prevDate, // startDate
}) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );
  const [startDate, setDate] = useState<any>(date);
  const [selectedTime, setSelectedTime] = useState<any>(date);
  const [isEntry, setIsEntry] = useState<any>(true);

  // **

  const handleTimeChange = (time: any) => {
    // Adjust the selected time to the nearest 15-minute interval
    const roundedMinutes = Math.ceil(moment(time).minutes() / 15) * 15;
    const adjustedTime = moment(time).minutes(roundedMinutes);
    setSelectedTime(adjustedTime.toDate());
  };

  const combinedDate = new Date(startDate);
  combinedDate.setHours(selectedTime.getHours());
  combinedDate.setMinutes(selectedTime.getMinutes());

  useEffect(() => {
    setIsEntry(title?.toLowerCase() === "sortie" ? false : true);
  }, [title]);

  useEffect(() => {
    getDate(combinedDate);
    // eslint-disable-next-line
  }, [startDate, selectedTime]);

  // ** maxTime
  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();

  useEffect(() => {
    if (!isEntry) {
      const timeBetween = moment(date).diff(moment(prevDate), "minutes");
      if (timeBetween < 60) {
        setSelectedTime(moment(prevDate).add(1, "hour").toDate());
      }
    }
    //eslint-disable-next-line
  }, [prevDate]);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
        borderLeft: { md: "none", xs: "1px solid #D9D9D9" },
        bgcolor: "white",
        borderRadius: { md: 0, sm: 0, xs: ".4rem" },
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t(title)}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              locale={currentLanguage}
              minDate={isEntry ? new Date() : minDate}
              selected={date}
              onChange={(date: any) => setDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={date}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1,
          cursor: "pointer",
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            <DatePicker
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="HH:mm"
              timeFormat="HH:mm"
              selected={selectedTime}
              onChange={handleTimeChange}
              minTime={minTime}
              maxTime={endOfDayAsDate}
              customInput={
                <CustomTimeInput
                  label={t("Sélectionner une date")}
                  start={selectedTime}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};
export const ExitDatePickerNoBorder: FC<any> = ({ getEndDate, startDate }) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );
  const minDateSortie = moment(startDate).toDate();
  const [endDate, setEndDate] = useState<any>(new Date());
  const [minTime, setMinTime] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState<any>(new Date());
  const handleTimeChange = (time: any) => {
    setSelectedTime(time);
  };

  useEffect(() => {
    getEndDate(endDate, selectedTime);
    // eslint-disable-next-line
  }, [endDate, selectedTime]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  useEffect(() => {
    const startDateTime = new Date(startDate);
    const minTime = new Date(startDateTime.getTime());
    // it should be + 30 but the time zone keeps on adding 1h
    minTime.setUTCMinutes(minTime.getUTCMinutes() - 30);

    setMinTime(minTime);
  }, [startDate]);

  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();

  return (
    <Box
      sx={{
        flex: 1,
        borderLeft: "1px solid #D9D9D9",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
        bgcolor: "white",
        borderRadius: { md: 0, sm: 0, xs: ".4rem" },
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Sortie")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              locale={currentLanguage}
              minDate={minDateSortie}
              selected={endDate}
              onChange={(date: any) => setEndDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={endDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            <DatePicker
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="HH:mm"
              timeFormat="HH:mm"
              selected={selectedTime}
              onChange={handleTimeChange}
              minTime={minTime}
              maxTime={endOfDayAsDate} // sets maximum time to end of the day
              customInput={
                <CustomTimeInput
                  label={t("Sélectionner une date")}
                  start={selectedTime}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const EntryDatePickerUpdate: FC<any> = ({
  getStartDate,
  reservStartDate,
}) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  // testing here -1 hour caused by the timezone / check the output in Belguim
  const minusHour = moment(reservStartDate).subtract({ hours: 1 }).format();

  const [startDate, setStartDate] = useState<any>(new Date(reservStartDate));
  const [selectedTime, setSelectedTime] = useState<any>(new Date(minusHour));
  const handleTimeChange = (time: any) => {
    setSelectedTime(time);
  };

  useEffect(() => {
    getStartDate(startDate, selectedTime);
    // eslint-disable-next-line
  }, [startDate, selectedTime]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  const minTime = moment().toDate();
  if (moment(startDate).isSame(moment(), "day")) {
    minTime.setHours(moment().hours());
    minTime.setMinutes(moment().minutes());
  } else {
    minTime.setHours(0);
    minTime.setMinutes(0);
  }
  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();

  // const localDate = moment(new Date(reservStartDate)).format();
  // const { timeZone: localTZ } = Intl.DateTimeFormat().resolvedOptions();
  // const apiTimeFromPickerLocalDate = moment.utc(localDate).format();

  // const pickerTimeFromApiTime = moment.utc(apiTimeFromPickerLocalDate).toDate();

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Entrée")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              locale={currentLanguage}
              minDate={new Date()}
              selected={startDate}
              onChange={(date: any) => setStartDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={startDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            <DatePicker
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="HH:mm"
              timeFormat="HH:mm"
              selected={selectedTime}
              onChange={handleTimeChange}
              minTime={minTime}
              maxTime={endOfDayAsDate} // sets maximum time to end of the day
              customInput={
                <CustomTimeInput
                  label={t("Sélectionner une date")}
                  start={selectedTime}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const ExitDatePickerUpdate: FC<any> = ({
  getEndDate,
  reservStartDate,
  reservEndDate,
}) => {
  // ** translation
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );
  const minusHour = moment(reservEndDate).subtract({ hours: 1 }).format();

  const minDateSortie = moment(reservStartDate).toDate();
  const [endDate, setEndDate] = useState<any>(new Date(reservEndDate));
  const [minTime, setMinTime] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState<any>(new Date(minusHour));
  const handleTimeChange = (time: any) => {
    setSelectedTime(time);
  };

  useEffect(() => {
    getEndDate(endDate, selectedTime);
    // eslint-disable-next-line
  }, [endDate, selectedTime]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  useEffect(() => {
    const startDateTime = new Date(reservStartDate);
    const minTime = new Date(startDateTime.getTime());
    // it should be + 30 but the time zone keeps on adding 1h
    minTime.setUTCMinutes(minTime.getUTCMinutes() - 30);

    setMinTime(minTime);
  }, [reservStartDate]);

  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Sortie")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              locale={currentLanguage}
              minDate={minDateSortie}
              selected={endDate}
              onChange={(date: any) => setEndDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={endDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            <DatePicker
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="HH:mm"
              timeFormat="HH:mm"
              selected={selectedTime}
              onChange={handleTimeChange}
              minTime={minTime}
              maxTime={endOfDayAsDate} // sets maximum time to end of the day
              customInput={
                <CustomTimeInput
                  label={t("Sélectionner une date")}
                  start={selectedTime}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};
export const InputTime: FC<any> = ({ value, onChange }) => {
  const { t } = useTranslation();

  return (
    <TimePickerWrapper>
      <DatePicker
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={5}
        dateFormat="HH:mm"
        timeFormat="HH:mm"
        selected={value}
        onChange={onChange}
        customInput={
          <CustomTimeInput label={t("Sélectionner une date")} start={value} />
        }
      />
    </TimePickerWrapper>
  );
};
export const TimeInput: FC<any> = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <TimePickerWrapper>
      <DatePicker
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={5}
        timeFormat="HH:mm"
        selected={value}
        onChange={onChange}
        customInput={
          <CustomInputTime label={t("Sélectionner une date")} start={value} />
        }
      />
    </TimePickerWrapper>
  );
};
/**Key account date time picker */
const calculateMaxDateForSkidataSimpleRes = (
  inputDateStr: any,
  totalMinites: any,
) => {
  const inputDate = new Date(inputDateStr);
  const resultDate = new Date(
    inputDate.getTime() + parseInt(totalMinites) * 60 * 1000,
  );
  return resultDate.toISOString();
};

export const PartnerEntryDatePicker: FC<any> = ({
  getStartDate,
  usePersonalizedDates,
  isDisabled,
  startTime,
}) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [startDate, setStartDate] = useState<any>(new Date());
  const [selectedTime, setSelectedTime] = useState<any>(new Date());
  const handleTimeChange = (time: any) => {
    setSelectedTime(time);
  };

  useEffect(() => {
    getStartDate(startDate, selectedTime, "globalStartTime");
    // eslint-disable-next-line
  }, [startDate, selectedTime]);

  useEffect(() => {
    if (typeof usePersonalizedDates === "boolean" && !usePersonalizedDates) {
      setStartDate(new Date());
      setSelectedTime(new Date());
    }
  }, [usePersonalizedDates]);

  useEffect(() => {
    if (startTime) {
      const currentTime = moment().utcOffset(0, true).startOf("minute");

      const startTimeStamp = moment.utc(startTime);

      if (currentTime.isAfter(startTimeStamp)) {
        const remainder = currentTime.minute() % 5;

        if (remainder !== 0) {
          currentTime.add(5 - remainder, "minutes");
        }

        const timeData = new Date(currentTime.toDate().getTime());

        const offsetInMinutes = moment().utcOffset();

        timeData.setUTCMinutes(timeData.getUTCMinutes() - offsetInMinutes);

        setStartDate(timeData);
        setSelectedTime(timeData);
      }
    }
  }, [startTime]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  const minTime = moment().toDate();
  if (moment(startDate).isSame(moment(), "day")) {
    minTime.setHours(moment().hours());
    minTime.setMinutes(moment().minutes());
  } else {
    minTime.setHours(0);
    minTime.setMinutes(0);
  }
  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();
  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Entrée")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              disabled={isDisabled}
              showYearDropdown
              locale={currentLanguage}
              minDate={new Date()}
              selected={startDate}
              onChange={(date: any) => setStartDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={startDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            <DatePicker
              disabled={isDisabled}
              locale={currentLanguage}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={5}
              dateFormat={currentLanguage === "en" ? "HH:mm" : "hh:mm"}
              timeFormat="HH:mm"
              selected={selectedTime}
              onChange={handleTimeChange}
              minTime={minTime}
              maxTime={endOfDayAsDate} // sets maximum time to end of the day
              customInput={
                <CustomInputTime
                  label={t("Sélectionner une date")}
                  start={selectedTime}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const PartnerExitDatePicker: FC<any> = ({
  getEndDate,
  startDate,
  finalEndDate,
  pmsType,
  ticketType,
  usePersonalizedDates,
  // isDisabled,
  applyMinimumDuration,
  reservationMinimumDuration,
  applyMaximumDuration,
  reservationMaximumDuration,
}) => {
  // ** translation
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [minEndDate, setMinEndDate] = useState<any>(new Date());
  const [endDate, setEndDate] = useState<any>(new Date());
  const [maxEndDate, setMaxEndDate] = useState<any>(new Date());

  const [minTime, setMinTime] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState<any>(new Date());
  const [maxTime, setMaxTime] = useState<any>(new Date());

  const [showFullTime, setShowFullTime] = useState(false);
  const [showEndOfDay, setShowEndOfDay] = useState(false);

  const handleTimeChange = (time: any) => {
    setSelectedTime(time);
  };

  useEffect(() => {
    getEndDate(endDate, selectedTime, "globalEndTime");
    // eslint-disable-next-line
  }, [endDate, selectedTime]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  useEffect(() => {
    if (
      typeof usePersonalizedDates === "boolean" &&
      !usePersonalizedDates &&
      startDate &&
      finalEndDate
    ) {
      // * SET MINIMUM DATE TIME
      let startDateTime = null;

      // * current min unit & duration
      const minUnit = reservationMinimumDuration?.unit;
      const minDuration = reservationMinimumDuration?.value;

      // * current max unit & duration
      const maxUnit = reservationMaximumDuration?.unit;
      const maxDuration = reservationMaximumDuration?.value;

      if (
        applyMinimumDuration &&
        reservationMinimumDuration &&
        ["Single Entry Reservation", "Réservation hockey"].includes(ticketType)
      ) {
        // * calculated min datetime
        const calculatedMinDate = moment
          .utc(startDate)
          .add(minDuration, minUnit);
        startDateTime = new Date(calculatedMinDate.toDate());
      } else {
        startDateTime = new Date(startDate);
      }

      const timeData = new Date(startDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      timeData.setUTCMinutes(timeData.getUTCMinutes() - 60);

      setMinEndDate(timeData);
      setMinTime(timeData);

      // const starterDate = moment.utc(startDate).format("YYYY-MM-DD");
      // const enderDate = moment.utc(finalEndDate).format("YYYY-MM-DD");

      const starterDate = moment.utc(startDate);
      const enderDate = moment.utc(finalEndDate);

      let maxValue: number;
      if (
        pmsType === "Skidata" &&
        (ticketType === "Single Entry Reservation" ||
          ticketType === "Réservation hockey")
      ) {
        maxValue = 89280;
      } else {
        maxValue = 5259600;
      }

      let getMaxdate = null;

      if (
        applyMaximumDuration &&
        reservationMaximumDuration &&
        ["Single Entry Reservation", "Réservation hockey"].includes(ticketType)
      ) {
        // * calculated min datetime
        const calculatedMaxDate = moment
          .utc(startDate)
          .add(maxDuration, maxUnit);
        getMaxdate = calculatedMaxDate.toISOString();
      } else {
        getMaxdate = calculateMaxDateForSkidataSimpleRes(startDate, maxValue);
      }

      // const maxDate = moment.utc(getMaxdate).format("YYYY-MM-DD");

      const minDate = moment(timeData).utcOffset(0, true);
      const maxDate = moment.utc(getMaxdate);

      const limitDateTime = new Date(getMaxdate);
      const maxDateTime = new Date(limitDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      maxDateTime.setUTCMinutes(maxDateTime.getUTCMinutes() - 60);

      setMaxEndDate(maxDateTime);

      // if (enderDate > maxDate) {
      //   setShowFullTime(true);
      //   setMaxTime(maxDateTime);
      //   setEndDate(maxDateTime);
      //   setSelectedTime(maxDateTime);
      //   setShowEndOfDay(false);
      // } else if (maxDate > enderDate) {
      //   setShowFullTime(true);
      //   setShowEndOfDay(true);
      // } else if (enderDate === maxDate) {
      //   setShowFullTime(true);
      //   setShowEndOfDay(false);
      //   setMaxTime(maxDateTime);
      // }

      if (enderDate.isAfter(maxDate, "days")) {
        setMaxTime(maxDateTime);
        setEndDate(maxDateTime);
        setSelectedTime(maxDateTime);
      } else if (enderDate.isSame(maxDate, "days")) {
        setMaxTime(maxDateTime);
      }

      if (starterDate.isSame(enderDate, "days")) {
        setMinTime(timeData);
      }

      const isAfter = moment.utc(startDate).isAfter(moment.utc(finalEndDate));

      if (isAfter) {
        setEndDate(timeData);
        setSelectedTime(timeData);
      }

      if (finalEndDate && startDate) {
        const minDurationDiff = moment
          .utc(finalEndDate)
          .diff(moment.utc(startDate), minUnit, true);

        const maxDurationDiff = moment
          .utc(finalEndDate)
          .diff(
            moment.utc(startDate),
            applyMaximumDuration ? maxUnit : "minutes",
            true,
          );

        const isMinDurationInvalid = minDurationDiff < minDuration;

        const selectedMaxDuration =
          applyMaximumDuration &&
          ["Single Entry Reservation", "Réservation hockey"].includes(
            ticketType,
          )
            ? maxDuration
            : maxValue;
        const isMaxDurationInvalid = maxDurationDiff > selectedMaxDuration;

        if (
          isMinDurationInvalid &&
          applyMinimumDuration &&
          ["Single Entry Reservation", "Réservation hockey"].includes(
            ticketType,
          )
        ) {
          setEndDate(timeData);
          setSelectedTime(timeData);
        }

        if (isMaxDurationInvalid) {
          setEndDate(maxDateTime);
          setSelectedTime(maxDateTime);
        }
      }

      // * handle showFullTime / showEndOfDay
      // 1. between end and max
      if (
        enderDate.isAfter(maxDate, "days") ||
        enderDate.isSame(maxDate, "days")
      ) {
        setShowFullTime(true);
        setShowEndOfDay(false);
      }

      if (maxDate.isAfter(enderDate, "days")) {
        setShowFullTime(true);
        setShowEndOfDay(true);
      }

      // between start & end
      if (starterDate.isSame(enderDate, "days")) {
        setShowFullTime(false);
        setShowEndOfDay(true);
      }

      if (
        enderDate.isAfter(starterDate, "days") &&
        !applyMinimumDuration &&
        !["Single Entry Reservation", "Réservation hockey"].includes(ticketType)
      ) {
        setShowFullTime(true);
      }

      if (
        minDate.isAfter(starterDate, "days") &&
        minDate.format("HH:mm") < starterDate.format("HH:mm")
      ) {
        setShowFullTime(false);
      }

      if (
        maxDate.isSame(starterDate, "days") &&
        maxDate.format("HH:mm") > starterDate.format("HH:mm")
      ) {
        setShowEndOfDay(false);
      }

      if (
        enderDate.isSame(maxDate, "days") &&
        !minDate.isSame(maxDate, "days")
      ) {
        setShowFullTime(true);
      }

      if (
        enderDate.isSame(maxDate, "days") &&
        minDate.isSame(maxDate, "days")
      ) {
        setShowFullTime(false);
      }

      // if (starterDate === enderDate) {
      //   setShowFullTime(false);
      //   setMinTime(timeData);
      //   //setSelectedTime(timeData);
      //   setShowEndOfDay(true);
      // }
      // if (enderDate > starterDate) {
      //   setShowFullTime(true);
      // } else {
      //   setShowFullTime(false);
      // }
    }
  }, [
    startDate,
    finalEndDate,
    ticketType,
    usePersonalizedDates,
    pmsType,
    applyMinimumDuration,
    reservationMinimumDuration,
    applyMaximumDuration,
    reservationMaximumDuration,
  ]);

  useEffect(() => {
    if (typeof usePersonalizedDates === "boolean" && !usePersonalizedDates) {
      setEndDate(new Date());
      setSelectedTime(new Date());
    }
  }, [usePersonalizedDates]);

  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Sortie")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              // disabled={isDisabled}
              showYearDropdown
              locale={currentLanguage}
              minDate={minEndDate}
              maxDate={
                maxEndDate
                // (pmsType === "Skidata" &&
                //   ticketType === "Single Entry Reservation") ||
                // applyMaximumDuration ? maxEndDate : undefined
              }
              selected={endDate}
              onChange={(date: any) => setEndDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={endDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            <DatePicker
              // disabled={isDisabled}
              locale={currentLanguage}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={5}
              dateFormat={currentLanguage === "en" ? "HH:mm" : "hh:mm"}
              timeFormat="HH:mm"
              selected={selectedTime}
              onChange={handleTimeChange}
              minTime={
                showFullTime
                  ? new Date(new Date().setHours(0, 0, 0, 0))
                  : minTime
              }
              maxTime={showEndOfDay ? endOfDayAsDate : maxTime}
              customInput={
                <CustomInputTime
                  label={t("Sélectionner une date")}
                  start={selectedTime}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const PartnerEntryDatePickerUpdate: FC<any> = ({
  getStartDate,
  reservStartDate,
  usePersonalizedDates,
  isDisabled,
}) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  // testing here -1 hour caused by the timezone / check the output in Belguim
  //const minusHour = moment(reservStartDate).subtract({ hours: 1 }).format();

  const [startDate, setStartDate] = useState<any>(new Date());
  const [selectedTime, setSelectedTime] = useState<any>(new Date());

  const handleTimeChange = (time: any) => {
    setSelectedTime(time);
  };

  useEffect(() => {
    getStartDate(startDate, selectedTime, "globalStartTime");
    // eslint-disable-next-line
  }, [startDate, selectedTime]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  const minTime = moment().toDate();
  if (moment(startDate).isSame(moment(), "day")) {
    minTime.setHours(moment().hours());
    minTime.setMinutes(moment().minutes());
  } else {
    minTime.setHours(0);
    minTime.setMinutes(0);
  }
  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();
  const currentTime = moment().utcOffset(0, true).seconds(0).milliseconds(0);

  const disableDatePicker = currentTime.isSameOrAfter(
    moment.utc(reservStartDate),
  );

  useEffect(() => {
    if (typeof usePersonalizedDates === "boolean" && !usePersonalizedDates) {
      if (reservStartDate) {
        const starterData = new Date(reservStartDate);
        const starterTimeData = new Date(starterData.getTime());
        const offsetInMinutes = moment().utcOffset();
        // it should be + 30 but the time zone keeps on adding 1h
        starterTimeData.setUTCMinutes(
          starterTimeData.getUTCMinutes() - offsetInMinutes,
        );

        setStartDate(starterTimeData);
        setSelectedTime(starterTimeData);
      }
      if (!reservStartDate) {
        setStartDate(new Date());
        setSelectedTime(new Date());
      }
    }
  }, [usePersonalizedDates, reservStartDate]);

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Entrée")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              disabled={disableDatePicker || isDisabled}
              showYearDropdown
              locale={currentLanguage}
              minDate={new Date()}
              selected={startDate}
              onChange={(date: any) => setStartDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={startDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            <DatePicker
              disabled={disableDatePicker || isDisabled}
              locale={currentLanguage}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={5}
              dateFormat={currentLanguage === "en" ? "HH:mm" : "hh:mm"}
              timeFormat="HH:mm"
              selected={selectedTime}
              onChange={handleTimeChange}
              minTime={minTime}
              maxTime={endOfDayAsDate} // sets maximum time to end of the day
              customInput={
                <CustomInputTime
                  label={t("Sélectionner une date")}
                  start={selectedTime}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const PartnerExitDatePickerUpdate: FC<any> = ({
  getEndDate,
  reservEndDate,
  startDate,
  finalEndDate,
  pmsType,
  ticketType,
  usePersonalizedDates,
  isDisabled,
  applyMinimumDuration,
  reservationMinimumDuration,
  applyMaximumDuration,
  reservationMaximumDuration,
}) => {
  // ** translation
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [minEndDate, setMinEndDate] = useState<any>(new Date());
  const [endDate, setEndDate] = useState<any>(new Date());
  const [maxEndDate, setMaxEndDate] = useState<any>(new Date());

  const [minTime, setMinTime] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState<any>(new Date());
  const [maxTime, setMaxTime] = useState<any>(new Date());

  const [showFullTime, setShowFullTime] = useState(false);
  const [showEndOfDay, setShowEndOfDay] = useState(false);

  const handleTimeChange = (time: any) => {
    setSelectedTime(time);
  };

  useEffect(() => {
    getEndDate(endDate, selectedTime, "globalEndTime");
    // eslint-disable-next-line
  }, [endDate, selectedTime]);
  // console.log(ticketType);
  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  useEffect(() => {
    if (
      typeof usePersonalizedDates === "boolean" &&
      !usePersonalizedDates &&
      startDate &&
      finalEndDate
    ) {
      // * SET MINIMUM DATE TIME
      let startDateTime = null;

      // * current min unit & duration
      const minUnit = reservationMinimumDuration?.unit;
      const minDuration = reservationMinimumDuration?.value;

      // * current max unit & duration
      const maxUnit = reservationMaximumDuration?.unit;
      const maxDuration = reservationMaximumDuration?.value;

      if (
        applyMinimumDuration &&
        reservationMinimumDuration &&
        ["Single Entry Reservation", "Réservation hockey"].includes(ticketType)
      ) {
        // * calculated min datetime
        const calculatedMinDate = moment
          .utc(startDate)
          .add(minDuration, minUnit);
        startDateTime = new Date(calculatedMinDate.toDate());
      } else {
        startDateTime = new Date(startDate);
      }

      const timeData = new Date(startDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      timeData.setUTCMinutes(timeData.getUTCMinutes() - 60);

      setMinEndDate(timeData);
      setMinTime(timeData);

      // const starterDate = moment.utc(startDate).format("YYYY-MM-DD");
      // const enderDate = moment.utc(finalEndDate).format("YYYY-MM-DD");

      const starterDate = moment.utc(startDate);
      const enderDate = moment.utc(finalEndDate);

      const isAfter = moment.utc(startDate).isAfter(moment.utc(finalEndDate));

      let maxValue: number;
      if (pmsType === "Skidata" && ticketType === "Single Entry Reservation") {
        maxValue = 89280;
      } else {
        maxValue = 5259600;
      }

      let getMaxdate = null;

      if (
        applyMaximumDuration &&
        reservationMaximumDuration &&
        ["Single Entry Reservation", "Réservation hockey"].includes(ticketType)
      ) {
        // * calculated min datetime
        const calculatedMaxDate = moment
          .utc(startDate)
          .add(maxDuration, maxUnit);
        getMaxdate = calculatedMaxDate.toISOString();
      } else {
        getMaxdate = calculateMaxDateForSkidataSimpleRes(startDate, maxValue);
      }

      // const maxDate = moment.utc(getMaxdate).format("YYYY-MM-DD");

      const minDate = moment(timeData).utcOffset(0, true);
      const maxDate = moment.utc(getMaxdate);

      const limitDateTime = new Date(getMaxdate);
      const maxDateTime = new Date(limitDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      maxDateTime.setUTCMinutes(maxDateTime.getUTCMinutes() - 60);

      setMaxEndDate(maxDateTime);

      // if (enderDate > maxDate) {
      //   setShowFullTime(true);
      //   setMaxTime(maxDateTime);
      //   setEndDate(maxDateTime);
      //   setSelectedTime(maxDateTime);
      //   setShowEndOfDay(false);
      // } else if (maxDate > enderDate) {
      //   setShowFullTime(true);
      //   setShowEndOfDay(true);
      // } else if (enderDate === maxDate) {
      //   setShowFullTime(true);
      //   setShowEndOfDay(false);
      //   setMaxTime(maxDateTime);
      // }

      if (enderDate.isAfter(maxDate, "days")) {
        setMaxTime(maxDateTime);
        setEndDate(maxDateTime);
        setSelectedTime(maxDateTime);
      } else if (enderDate.isSame(maxDate, "days")) {
        setMaxTime(maxDateTime);
      }

      if (starterDate.isSame(enderDate, "days")) {
        setMinTime(timeData);
      }

      if (isAfter) {
        setEndDate(timeData);
        setSelectedTime(timeData);
      }

      if (finalEndDate && startDate) {
        const minDurationDiff = moment
          .utc(finalEndDate)
          .diff(moment.utc(startDate), minUnit, true);

        const maxDurationDiff = moment
          .utc(finalEndDate)
          .diff(
            moment.utc(startDate),
            applyMaximumDuration ? maxUnit : "minutes",
            true,
          );

        const isMinDurationInvalid = minDurationDiff < minDuration;

        const selectedMaxDuration =
          applyMaximumDuration &&
          ["Single Entry Reservation", "Réservation hockey"].includes(
            ticketType,
          )
            ? maxDuration
            : maxValue;
        const isMaxDurationInvalid = maxDurationDiff > selectedMaxDuration;

        if (
          isMinDurationInvalid &&
          applyMinimumDuration &&
          ["Single Entry Reservation", "Réservation hockey"].includes(
            ticketType,
          )
        ) {
          setEndDate(timeData);
          setSelectedTime(timeData);
        }

        if (isMaxDurationInvalid) {
          setEndDate(maxDateTime);
          setSelectedTime(maxDateTime);
        }
      }

      // * handle showFullTime / showEndOfDay
      // 1. between end and max
      if (
        enderDate.isAfter(maxDate, "days") ||
        enderDate.isSame(maxDate, "days")
      ) {
        setShowFullTime(true);
        setShowEndOfDay(false);
      }

      if (maxDate.isAfter(enderDate, "days")) {
        setShowFullTime(true);
        setShowEndOfDay(true);
      }

      // between start & end
      if (starterDate.isSame(enderDate, "days")) {
        setShowFullTime(false);
        setShowEndOfDay(true);
      }

      if (
        enderDate.isAfter(starterDate, "days") &&
        !applyMinimumDuration &&
        !["Single Entry Reservation", "Réservation hockey"].includes(ticketType)
      ) {
        setShowFullTime(true);
      }

      if (
        minDate.isAfter(starterDate, "days") &&
        minDate.format("HH:mm") < starterDate.format("HH:mm")
      ) {
        setShowFullTime(false);
      }

      if (
        maxDate.isSame(starterDate, "days") &&
        maxDate.format("HH:mm") > starterDate.format("HH:mm")
      ) {
        setShowEndOfDay(false);
      }

      if (
        enderDate.isSame(maxDate, "days") &&
        !minDate.isSame(maxDate, "days")
      ) {
        setShowFullTime(true);
      }

      if (
        enderDate.isSame(maxDate, "days") &&
        minDate.isSame(maxDate, "days")
      ) {
        setShowFullTime(false);
      }

      // if (starterDate === enderDate) {
      //   setShowFullTime(false);
      //   setMinTime(timeData);
      //   //setSelectedTime(timeData);
      //   setShowEndOfDay(true);
      // }

      // if (enderDate > starterDate) {
      //   setShowFullTime(true);
      // } else {
      //   setShowFullTime(false);
      // }
    }
  }, [
    startDate,
    finalEndDate,
    ticketType,
    pmsType,
    usePersonalizedDates,
    applyMinimumDuration,
    reservationMinimumDuration,
    applyMaximumDuration,
    reservationMaximumDuration,
  ]);

  useEffect(() => {
    if (!usePersonalizedDates) {
      if (reservEndDate) {
        const starterEndData = new Date(reservEndDate);
        const starterTimeData = new Date(starterEndData.getTime());
        const offsetInMinutes = moment().utcOffset();
        // it should be + 30 but the time zone keeps on adding 1h
        starterTimeData.setUTCMinutes(
          starterTimeData.getUTCMinutes() - offsetInMinutes,
        );
        setEndDate(starterTimeData);
        setSelectedTime(starterTimeData);
      }

      if (!reservEndDate) {
        setEndDate(new Date());
        setSelectedTime(new Date());
      }
    }
  }, [reservEndDate, usePersonalizedDates]);

  const endOfDay = moment().endOf("day");
  const endOfDayAsDate = endOfDay.toDate();

  // console.log({ showEndOfDay, maxTime });
  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Sortie")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              showYearDropdown
              disabled={isDisabled}
              locale={currentLanguage}
              minDate={minEndDate}
              maxDate={
                maxEndDate
                // pmsType === "Skidata" &&
                // ticketType === "Single Entry Reservation"
                //   ? maxEndDate
                //   : undefined
              }
              selected={endDate}
              onChange={(date: any) => setEndDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={endDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            <DatePicker
              disabled={isDisabled}
              locale={currentLanguage}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={5}
              dateFormat={"HH:mm"}
              timeFormat="HH:mm"
              selected={selectedTime}
              onChange={handleTimeChange}
              minTime={
                showFullTime
                  ? new Date(new Date().setHours(0, 0, 0, 0))
                  : minTime
              }
              maxTime={showEndOfDay ? endOfDayAsDate : maxTime} // sets maximum time to end of the day
              customInput={
                <CustomInputTime
                  label={t("Sélectionner une date")}
                  start={selectedTime}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

//calculate max end date
const calculateMaxDate = (inputDateStr: any, daysLimit: any) => {
  const inputDate = new Date(inputDateStr);
  const resultDate = new Date(
    inputDate.getTime() + parseInt(daysLimit) * 24 * 60 * 60 * 1000,
  );
  return resultDate.toISOString();
};

//UCL date time picker
export const UclEntryDatePicker: FC<any> = ({ getStartDate }) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [startDate, setStartDate] = useState<any>(new Date());
  const [selectedTime, setSelectedTime] = useState<any>(new Date());
  const handleTimeChange = (time: any) => {
    setSelectedTime(time);
  };

  useEffect(() => {
    const times = moment().toDate();
    const currentDateTime = moment();
    const startDateTime = moment(startDate);

    const sameDate = currentDateTime.isSame(startDateTime, "day");
    const isStartDateTimeBeforeCurrent =
      currentDateTime.format("HH:mm") > startDateTime.format("HH:mm");

    if (sameDate && isStartDateTimeBeforeCurrent) {
      times.setHours(moment().hours());
      times.setMinutes(moment().minutes());
      setSelectedTime(times);
    }
    // eslint-disable-next-line
  }, [startDate]);

  useEffect(() => {
    getStartDate(startDate, selectedTime);
    // eslint-disable-next-line
  }, [startDate, selectedTime]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  const minTime = moment().toDate();
  if (moment(startDate).isSame(moment(), "day")) {
    minTime.setHours(moment().hours());
    minTime.setMinutes(moment().minutes());
  } else {
    minTime.setHours(0);
    minTime.setMinutes(0);
  }
  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();
  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Entrée")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              showYearDropdown
              locale={currentLanguage}
              minDate={new Date()}
              selected={startDate}
              onChange={(date: any) => setStartDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={startDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            <DatePicker
              locale={currentLanguage}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={5}
              dateFormat={currentLanguage === "en" ? "HH:mm" : "hh:mm"}
              timeFormat="HH:mm"
              selected={selectedTime}
              onChange={handleTimeChange}
              minTime={minTime}
              maxTime={endOfDayAsDate} // sets maximum time to end of the day
              customInput={
                <CustomInputTime
                  label={t("Sélectionner une date")}
                  start={selectedTime}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const UclExitDatePicker: FC<any> = ({
  getEndDate,
  startDate,
  finalEndDate,
  applyMinimumDuration,
  reservationMinimumDuration,
  applyMaximumDuration,
  reservationMaximumDuration,
}) => {
  // ** translation
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [minEndDate, setMinEndDate] = useState<any>(new Date());
  const [endDate, setEndDate] = useState<any>(new Date());
  const [maxEndDate, setMaxEndDate] = useState<any>(new Date());

  const [selectedTime, setSelectedTime] = useState<any>(new Date());
  const [minTime, setMinTime] = useState<any>(new Date());
  const [maxTime, setMaxTime] = useState<any>(new Date());

  const [showFullTime, setShowFullTime] = useState(false);
  const [showEndOfDay, setShowEndOfDay] = useState(false);

  const handleTimeChange = (time: any) => {
    setSelectedTime(time);
  };

  useEffect(() => {
    getEndDate(endDate, selectedTime);
    // eslint-disable-next-line
  }, [endDate, selectedTime]);

  useEffect(() => {
    // * SET MINIMUM DATE TIME
    let startDateTime = null;

    // * current min unit & duration
    const minUnit = reservationMinimumDuration?.unit;
    const minDuration = reservationMinimumDuration?.value;

    // * current max unit & duration
    const maxUnit = reservationMaximumDuration?.unit;
    const maxDuration = reservationMaximumDuration?.value;

    if (applyMinimumDuration && reservationMinimumDuration) {
      // * calculated min datetime
      const calculatedMinDate = moment.utc(startDate).add(minDuration, minUnit);
      startDateTime = new Date(calculatedMinDate.toDate());
    } else {
      startDateTime = new Date(startDate);
    }

    const timeData = new Date(startDateTime.getTime());
    // it should be + 30 but the time zone keeps on adding 1h
    timeData.setUTCMinutes(timeData.getUTCMinutes() - 60);

    setMinEndDate(timeData);
    setMinTime(timeData);

    // const starterDate = moment.utc(startDate).format("YYYY-MM-DD");
    // const enderDate = moment.utc(finalEndDate).format("YYYY-MM-DD");

    const starterDate = moment.utc(startDate);
    const enderDate = moment.utc(finalEndDate);

    let getMaxdate = null;

    if (applyMaximumDuration && reservationMaximumDuration) {
      // * calculated min datetime
      const calculatedMaxDate = moment.utc(startDate).add(maxDuration, maxUnit);
      getMaxdate = calculatedMaxDate.toISOString();
    } else {
      getMaxdate = calculateMaxDate(startDate, 15);
    }

    // const maxDate = moment.utc(getMaxdate).format("YYYY-MM-DD");
    const minDate = moment(timeData).utcOffset(0, true);
    const maxDate = moment.utc(getMaxdate);

    const limitDateTime = new Date(getMaxdate);
    const maxDateTime = new Date(limitDateTime.getTime());
    // it should be + 30 but the time zone keeps on adding 1h
    maxDateTime.setUTCMinutes(maxDateTime.getUTCMinutes() - 60);

    setMaxEndDate(maxDateTime);

    const isAfter = moment.utc(startDate).isAfter(moment.utc(finalEndDate));

    if (finalEndDate && startDate) {
      const minDurationDiff = moment
        .utc(finalEndDate)
        .diff(moment.utc(startDate), minUnit, true);

      const maxDurationDiff = moment
        .utc(finalEndDate)
        .diff(
          moment.utc(startDate),
          applyMaximumDuration ? maxUnit : "days",
          true,
        );

      const isMinDurationInvalid = minDurationDiff < minDuration;

      const selectedMaxDuration = applyMaximumDuration ? maxDuration : 15;
      const isMaxDurationInvalid = maxDurationDiff > selectedMaxDuration;

      if (isMinDurationInvalid && applyMinimumDuration) {
        setEndDate(timeData);
        setSelectedTime(timeData);
      }

      if (isMaxDurationInvalid) {
        setEndDate(maxDateTime);
        setSelectedTime(maxDateTime);
      }
    }

    if (isAfter) {
      setEndDate(timeData);
      setSelectedTime(timeData);
    }

    // if (starterDate === enderDate) {
    //   setShowFullTime(false);
    //   setMinTime(timeData);
    //   //setSelectedTime(timeData);
    //   setShowEndOfDay(true);
    // } else if (enderDate > maxDate) {
    //   setShowFullTime(true);
    //   setMaxTime(maxDateTime);
    //   setEndDate(maxDateTime);
    //   setSelectedTime(maxDateTime);
    //   setShowEndOfDay(false);
    // } else if (maxDate > enderDate) {
    //   setShowFullTime(true);
    //   setShowEndOfDay(true);
    // } else if (enderDate === maxDate) {
    //   setShowFullTime(true);
    //   setShowEndOfDay(false);
    //   setMaxTime(maxDateTime);
    // }

    if (enderDate.isAfter(maxDate, "days")) {
      setMaxTime(maxDateTime);
      setEndDate(maxDateTime);
      setSelectedTime(maxDateTime);
    } else if (enderDate.isSame(maxDate, "days")) {
      setMaxTime(maxDateTime);
    }

    if (starterDate.isSame(enderDate, "days")) {
      setMinTime(timeData);
    }

    // * handle showFullTime / showEndOfDay
    // 1. between end and max
    if (
      enderDate.isAfter(maxDate, "days") ||
      enderDate.isSame(maxDate, "days")
    ) {
      setShowFullTime(true);
      setShowEndOfDay(false);
    }

    if (maxDate.isAfter(enderDate, "days")) {
      setShowFullTime(true);
      setShowEndOfDay(true);
    }

    // between start & end
    if (starterDate.isSame(enderDate, "days")) {
      setShowFullTime(false);
      setShowEndOfDay(true);
    }

    if (enderDate.isAfter(starterDate, "days") && !applyMinimumDuration) {
      setShowFullTime(true);
    }

    if (
      minDate.isAfter(starterDate, "days") &&
      minDate.format("HH:mm") < starterDate.format("HH:mm")
    ) {
      setShowFullTime(false);
    }

    if (
      maxDate.isSame(starterDate, "days") &&
      maxDate.format("HH:mm") > starterDate.format("HH:mm")
    ) {
      setShowEndOfDay(false);
    }

    if (enderDate.isSame(maxDate, "days") && !minDate.isSame(maxDate, "days")) {
      setShowFullTime(true);
    }

    if (enderDate.isSame(maxDate, "days") && minDate.isSame(maxDate, "days")) {
      setShowFullTime(false);
    }
  }, [
    startDate,
    finalEndDate,
    applyMinimumDuration,
    reservationMinimumDuration,
    applyMaximumDuration,
    reservationMaximumDuration,
  ]);

  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();

  const handleDate = (date: any) => {
    setEndDate(date);
  };

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Sortie")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              showYearDropdown
              locale={currentLanguage}
              minDate={minEndDate}
              maxDate={maxEndDate}
              selected={endDate}
              onChange={handleDate}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={endDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            <DatePicker
              locale={currentLanguage}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={5}
              dateFormat={currentLanguage === "en" ? "HH:mm" : "hh:mm"}
              timeFormat="HH:mm"
              selected={selectedTime}
              onChange={handleTimeChange}
              minTime={
                showFullTime
                  ? new Date(new Date().setHours(0, 0, 0, 0))
                  : minTime
              }
              maxTime={showEndOfDay ? endOfDayAsDate : maxTime}
              customInput={
                <CustomInputTime
                  label={t("Sélectionner une date")}
                  start={selectedTime}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const UclEntryDatePickerUpdate: FC<any> = ({
  getStartDate,
  reservStartDate,
}) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const currentTime = moment().utcOffset(0, true).startOf("minute");
  const reservStartDateTime = moment.utc(reservStartDate).startOf("minute");

  const disableStartTimeDatePicker =
    currentTime.isSameOrAfter(reservStartDateTime);
  // testing here -1 hour caused by the timezone / check the output in Belguim
  const starterData = new Date(reservStartDate);
  const starterTimeData = new Date(starterData.getTime());
  // it should be + 30 but the time zone keeps on adding 1h
  starterTimeData.setUTCMinutes(starterTimeData.getUTCMinutes() - 60);

  const [startDate, setStartDate] = useState<any>(starterTimeData);
  const [selectedTime, setSelectedTime] = useState<any>(starterTimeData);
  const handleTimeChange = (time: any) => {
    setSelectedTime(time);
  };

  useEffect(() => {
    const times = moment().toDate();
    const currentDateTime = moment();
    const startDateTime = moment(startDate);

    const sameDate = currentDateTime.isSame(startDateTime, "day");
    const isStartDateTimeBeforeCurrent =
      currentDateTime.format("HH:mm") > startDateTime.format("HH:mm");

    if (sameDate && isStartDateTimeBeforeCurrent) {
      times.setHours(moment().hours());
      times.setMinutes(moment().minutes());
      setSelectedTime(times);
    }
    // eslint-disable-next-line
  }, [startDate]);

  useEffect(() => {
    getStartDate(startDate, selectedTime);
    // eslint-disable-next-line
  }, [startDate, selectedTime]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  const minTime = moment().toDate();
  if (moment(startDate).isSame(moment(), "day")) {
    minTime.setHours(moment().hours());
    minTime.setMinutes(moment().minutes());
  } else {
    minTime.setHours(0);
    minTime.setMinutes(0);
  }
  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();
  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Entrée")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              disabled={disableStartTimeDatePicker}
              showYearDropdown
              locale={currentLanguage}
              minDate={new Date()}
              selected={startDate}
              onChange={(date: any) => setStartDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={startDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            <DatePicker
              disabled={disableStartTimeDatePicker}
              locale={currentLanguage}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat={currentLanguage === "en" ? "HH:mm" : "hh:mm"}
              timeFormat="HH:mm"
              selected={selectedTime}
              onChange={handleTimeChange}
              minTime={minTime}
              maxTime={endOfDayAsDate} // sets maximum time to end of the day
              customInput={
                <CustomInputTime
                  label={t("Sélectionner une date")}
                  start={selectedTime}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const UclExitDatePickerUpdate: FC<any> = ({
  getEndDate,
  reservEndDate,
  startDate,
  finalEndDate,
  applyMinimumDuration,
  reservationMinimumDuration,
  applyMaximumDuration,
  reservationMaximumDuration,
}) => {
  // ** translation
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const starterEndData = new Date(reservEndDate);
  const starterTimeData = new Date(starterEndData.getTime());
  // it should be + 30 but the time zone keeps on adding 1h
  starterTimeData.setUTCMinutes(starterTimeData.getUTCMinutes() - 60);

  const [minEndDate, setMinEndDate] = useState<any>(new Date());
  const [endDate, setEndDate] = useState<any>(starterTimeData);
  const [maxEndDate, setMaxEndDate] = useState<any>(new Date());

  const [selectedTime, setSelectedTime] = useState<any>(starterTimeData);
  const [minTime, setMinTime] = useState<any>(new Date());
  const [maxTime, setMaxTime] = useState<any>(new Date());

  const [showFullTime, setShowFullTime] = useState(false);
  const [showEndOfDay, setShowEndOfDay] = useState(false);

  const handleTimeChange = (time: any) => {
    setSelectedTime(time);
  };

  useEffect(() => {
    getEndDate(endDate, selectedTime);
    // eslint-disable-next-line
  }, [endDate, selectedTime]);

  // useEffect(() => {
  //   if (moment(endDate).isAfter(moment(maxEndDate), "day")) {
  //     setEndDate(new Date(maxEndDate));
  //   }
  // }, [endDate, maxEndDate]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  useEffect(() => {
    // * SET MINIMUM DATE TIME
    let startDateTime = null;

    // * current min unit & duration
    const minUnit = reservationMinimumDuration?.unit;
    const minDuration = reservationMinimumDuration?.value;

    // * current max unit & duration
    const maxUnit = reservationMaximumDuration?.unit;
    const maxDuration = reservationMaximumDuration?.value;

    // * calculated min datetime
    if (applyMinimumDuration && reservationMinimumDuration) {
      // * calculated min datetime
      const calculatedMinDate = moment.utc(startDate).add(minDuration, minUnit);
      startDateTime = new Date(calculatedMinDate.toDate());
    } else {
      startDateTime = new Date(startDate);
    }

    const timeData = new Date(startDateTime.getTime());
    // it should be + 30 but the time zone keeps on adding 1h
    timeData.setUTCMinutes(timeData.getUTCMinutes() - 60);

    setMinEndDate(timeData);
    setMinTime(timeData);

    // const starterDate = moment.utc(startDate).format("YYYY-MM-DD");
    // const enderDate = moment.utc(finalEndDate).format("YYYY-MM-DD");

    const starterDate = moment.utc(startDate);
    const enderDate = moment.utc(finalEndDate);

    // * max date
    let getMaxdate = null;

    if (applyMaximumDuration && reservationMaximumDuration) {
      // * calculated min datetime
      const calculatedMaxDate = moment.utc(startDate).add(maxDuration, maxUnit);
      getMaxdate = calculatedMaxDate.toISOString();
    } else {
      getMaxdate = calculateMaxDate(startDate, 15);
    }

    // const maxDate = moment.utc(getMaxdate).format("YYYY-MM-DD");
    const minDate = moment(timeData).utcOffset(0, true);
    const maxDate = moment.utc(getMaxdate);

    const limitDateTime = new Date(getMaxdate);
    const maxDateTime = new Date(limitDateTime.getTime());
    // it should be + 30 but the time zone keeps on adding 1h
    maxDateTime.setUTCMinutes(maxDateTime.getUTCMinutes() - 60);

    setMaxEndDate(maxDateTime);

    const isAfter = moment.utc(startDate).isAfter(moment.utc(finalEndDate));

    if (finalEndDate && startDate) {
      const minDurationDiff = moment
        .utc(finalEndDate)
        .diff(moment.utc(startDate), minUnit, true);

      const maxDurationDiff = moment
        .utc(finalEndDate)
        .diff(
          moment.utc(startDate),
          applyMaximumDuration ? maxUnit : "days",
          true,
        );

      const isMinDurationInvalid = minDurationDiff < minDuration;

      const selectedMaxDuration = applyMaximumDuration ? maxDuration : 15;
      const isMaxDurationInvalid = maxDurationDiff > selectedMaxDuration;

      if (isMinDurationInvalid && applyMinimumDuration) {
        setEndDate(timeData);
        setSelectedTime(timeData);
      }

      if (isMaxDurationInvalid) {
        setEndDate(maxDateTime);
        setSelectedTime(maxDateTime);
      }
    }

    if (isAfter) {
      setEndDate(timeData);
      setSelectedTime(timeData);
    }

    // if (starterDate === enderDate) {
    //   setShowFullTime(false);
    //   setMinTime(timeData);
    //   //setSelectedTime(timeData);
    //   setShowEndOfDay(true);
    // } else if (enderDate > maxDate) {
    //   setShowFullTime(true);
    //   setMaxTime(maxDateTime);
    //   setEndDate(maxDateTime);
    //   setSelectedTime(maxDateTime);
    //   setShowEndOfDay(false);
    // } else if (maxDate > enderDate) {
    //   setShowFullTime(true);
    //   setShowEndOfDay(true);
    // } else if (enderDate === maxDate) {
    //   setShowFullTime(true);
    //   setShowEndOfDay(false);
    //   setMaxTime(maxDateTime);
    // }

    if (enderDate.isAfter(maxDate, "days")) {
      setMaxTime(maxDateTime);
      setEndDate(maxDateTime);
      setSelectedTime(maxDateTime);
    } else if (enderDate.isSame(maxDate, "days")) {
      setMaxTime(maxDateTime);
    }

    if (starterDate.isSame(enderDate, "days")) {
      setMinTime(timeData);
    }

    // * handle showFullTime / showEndOfDay
    // 1. between end and max
    if (
      enderDate.isAfter(maxDate, "days") ||
      enderDate.isSame(maxDate, "days")
    ) {
      setShowFullTime(true);
      setShowEndOfDay(false);
    }

    if (maxDate.isAfter(enderDate, "days")) {
      setShowFullTime(true);
      setShowEndOfDay(true);
    }

    // between start & end
    if (starterDate.isSame(enderDate, "days")) {
      setShowFullTime(false);
      setShowEndOfDay(true);
    }

    if (enderDate.isAfter(starterDate, "days") && !applyMinimumDuration) {
      setShowFullTime(true);
    }

    if (
      minDate.isAfter(starterDate, "days") &&
      minDate.format("HH:mm") < starterDate.format("HH:mm")
    ) {
      setShowFullTime(false);
    }

    if (
      maxDate.isSame(starterDate, "days") &&
      maxDate.format("HH:mm") > starterDate.format("HH:mm")
    ) {
      setShowEndOfDay(false);
    }

    if (enderDate.isSame(maxDate, "days") && !minDate.isSame(maxDate, "days")) {
      setShowFullTime(true);
    }

    if (enderDate.isSame(maxDate, "days") && minDate.isSame(maxDate, "days")) {
      setShowFullTime(false);
    }
  }, [
    startDate,
    finalEndDate,
    applyMinimumDuration,
    reservationMinimumDuration,
    applyMaximumDuration,
    reservationMaximumDuration,
  ]);

  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();

  const handleDate = (date: any) => {
    setEndDate(date);
  };

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Sortie")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              showYearDropdown
              locale={currentLanguage}
              minDate={minEndDate}
              maxDate={maxEndDate}
              selected={endDate}
              onChange={handleDate}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={endDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Heure")}
          </Typography>
          <TimePickerWrapper>
            <DatePicker
              locale={currentLanguage}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={5}
              dateFormat={currentLanguage === "en" ? "HH:mm" : "hh:mm"}
              timeFormat="HH:mm"
              selected={selectedTime}
              onChange={handleTimeChange}
              minTime={
                showFullTime
                  ? new Date(new Date().setHours(0, 0, 0, 0))
                  : minTime
              }
              maxTime={showEndOfDay ? endOfDayAsDate : maxTime} // sets maximum time to end of the day
              customInput={
                <CustomInputTime
                  label={t("Sélectionner une date")}
                  start={selectedTime}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

// key Account Subscription
export const PartnerSubscriptionEntryDatePicker: FC<any> = ({
  getStartDate,
  usePersonalizedDates,
  isDisabled,
}) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [startDate, setStartDate] = useState<any>(new Date());

  useEffect(() => {
    getStartDate(startDate, new Date().setHours(0, 0, 0, 0), "globalStartTime");
    // eslint-disable-next-line
  }, [startDate]);

  useEffect(() => {
    if (typeof usePersonalizedDates === "boolean" && !usePersonalizedDates) {
      setStartDate(new Date());
    }
  }, [usePersonalizedDates]);

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Entrée")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              showYearDropdown
              disabled={isDisabled}
              locale={currentLanguage}
              minDate={new Date()}
              selected={startDate}
              onChange={(date: any) => setStartDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={startDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const PartnerSubscriptionExitDatePicker: FC<any> = ({
  getEndDate,
  startDate,
  finalEndDate,
  usePersonalizedDates,
  isDisabled,
  applyMinimumDuration,
  subscriptionMinimumDuration,
  applyMaximumDuration,
  subscriptionMaximumDuration,
}) => {
  // ** translation
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [minEndDate, setMinEndDate] = useState<any>(new Date());
  const [endDate, setEndDate] = useState<any>(new Date());
  const [maxEndDate, setMaxEndDate] = useState<any>(new Date());

  useEffect(() => {
    getEndDate(endDate, new Date().setHours(0, 0, 0, 0), "globalEndTime");
    // eslint-disable-next-line
  }, [endDate]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  useEffect(() => {
    if (
      typeof usePersonalizedDates === "boolean" &&
      !usePersonalizedDates &&
      startDate &&
      finalEndDate
    ) {
      // * SET MINIMUM DATE TIME
      let startDateTime = null;

      // * current min unit & duration
      const minUnit = subscriptionMinimumDuration?.unit;
      const minDuration = subscriptionMinimumDuration?.value;

      // * current max unit & duration
      const maxUnit = subscriptionMaximumDuration?.unit;
      const maxDuration = subscriptionMaximumDuration?.value;

      // * calculated min datetime
      if (applyMinimumDuration && subscriptionMinimumDuration) {
        const calculatedMinDate = moment
          .utc(startDate)
          .add(minDuration, minUnit);
        startDateTime = new Date(calculatedMinDate.toDate());
      } else {
        const getMindate = moment.utc(startDate).add(1, "month").startOf("day");
        startDateTime = new Date(getMindate.toDate());
      }

      const minDateTime = new Date(startDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      minDateTime.setUTCMinutes(minDateTime.getUTCMinutes() - 60);

      setMinEndDate(minDateTime);

      // * calculated max date
      let getMaxdate = null;
      if (applyMaximumDuration && subscriptionMaximumDuration) {
        getMaxdate = moment.utc(startDate).add(maxDuration, maxUnit).toDate();
      } else {
        getMaxdate = moment.utc(startDate).add(10, "years").toDate();
      }

      const limitDateTime = new Date(getMaxdate);
      const maxDateTime = new Date(limitDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      maxDateTime.setUTCMinutes(maxDateTime.getUTCMinutes() - 60);

      setMaxEndDate(maxDateTime);

      const enderDate = moment.utc(finalEndDate).format("YYYY-MM-DD");
      const maxDate = moment.utc(getMaxdate).format("YYYY-MM-DD");

      const isAfter = moment
        .utc(finalEndDate)
        .isBefore(moment.utc(minDateTime));

      if (finalEndDate && startDate) {
        const minDurationDiff = moment
          .utc(finalEndDate)
          .diff(
            moment.utc(startDate),
            applyMinimumDuration ? minUnit : "months",
            true,
          );

        const maxDurationDiff = moment
          .utc(finalEndDate)
          .diff(
            moment.utc(startDate),
            applyMaximumDuration ? maxUnit : "years",
            true,
          );
        const selectedMinDuration = applyMinimumDuration ? minDuration : 1;
        const isMinDurationInvalid = minDurationDiff < selectedMinDuration;

        const selectedMaxDuration = applyMaximumDuration ? maxDuration : 10;
        const isMaxDurationInvalid = maxDurationDiff > selectedMaxDuration;

        if (isMinDurationInvalid) {
          setEndDate(minDateTime);
        }

        if (isMaxDurationInvalid) {
          setEndDate(maxDateTime);
        }
      } else if (enderDate > maxDate) {
        setEndDate(maxDateTime);
      } else if (isAfter) {
        setEndDate(minDateTime);
      }
    }
  }, [
    startDate,
    finalEndDate,
    usePersonalizedDates,
    applyMinimumDuration,
    subscriptionMinimumDuration,
    applyMaximumDuration,
    subscriptionMaximumDuration,
  ]);

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Sortie")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              showYearDropdown
              disabled={isDisabled}
              locale={currentLanguage}
              minDate={minEndDate}
              maxDate={maxEndDate}
              selected={endDate}
              onChange={(date: any) => setEndDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={endDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const PartnerSubscriptionEntryDatePickerUpdate: FC<any> = ({
  getStartDate,
  initStartDate,
  usePersonalizedDates,
  isDisabled,
}) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [startDate, setStartDate] = useState<any>(new Date());

  useEffect(() => {
    getStartDate(startDate, new Date().setHours(0, 0, 0, 0), "globalStartTime");
    // eslint-disable-next-line
  }, [startDate]);

  useEffect(() => {
    if (typeof usePersonalizedDates === "boolean" && !usePersonalizedDates) {
      if (initStartDate) {
        const starterData = new Date(initStartDate);
        const starterTimeData = new Date(starterData.getTime());
        const offsetInMinutes = moment().utcOffset();
        // it should be + 30 but the time zone keeps on adding 1h
        starterTimeData.setUTCMinutes(
          starterTimeData.getUTCMinutes() - offsetInMinutes,
        );
        setStartDate(starterTimeData);
      }
      if (!initStartDate) {
        setStartDate(new Date());
      }
    }
  }, [usePersonalizedDates, initStartDate]);

  const currentTime = moment().utcOffset(0, true).seconds(0).milliseconds(0);
  const disableDatePicker = currentTime.isSameOrAfter(
    moment.utc(initStartDate),
  );

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Entrée")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              disabled={disableDatePicker || isDisabled}
              showYearDropdown
              locale={currentLanguage}
              minDate={new Date()}
              selected={startDate}
              onChange={(date: any) => setStartDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={startDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const PartnerSubscriptionExitDatePickerUpdate: FC<any> = ({
  getEndDate,
  initEndDate,
  startDate,
  finalEndDate,
  usePersonalizedDates,
  isDisabled,
  applyMinimumDuration,
  subscriptionMinimumDuration,
  applyMaximumDuration,
  subscriptionMaximumDuration,
}) => {
  // ** translation
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const starterEndData = new Date(initEndDate);
  const starterTimeData = new Date(starterEndData.getTime());
  const offsetInMinutes = moment().utcOffset();
  // it should be + 30 but the time zone keeps on adding 1h
  starterTimeData.setUTCMinutes(
    starterTimeData.getUTCMinutes() - offsetInMinutes,
  );

  const [minEndDate, setMinEndDate] = useState<any>(new Date());
  const [endDate, setEndDate] = useState<any>(starterTimeData);
  const [maxEndDate, setMaxEndDate] = useState<any>(new Date());

  useEffect(() => {
    getEndDate(endDate, new Date().setHours(0, 0, 0, 0), "globalEndTime");
    // eslint-disable-next-line
  }, [endDate]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  useEffect(() => {
    if (
      typeof usePersonalizedDates === "boolean" &&
      !usePersonalizedDates &&
      startDate &&
      finalEndDate
    ) {
      // * SET MINIMUM DATE TIME
      let startDateTime = null;

      // * current min unit & duration
      const minUnit = subscriptionMinimumDuration?.unit;
      const minDuration = subscriptionMinimumDuration?.value;

      // * current max unit & duration
      const maxUnit = subscriptionMaximumDuration?.unit;
      const maxDuration = subscriptionMaximumDuration?.value;

      // * calculated min datetime
      if (applyMinimumDuration && subscriptionMinimumDuration) {
        const calculatedMinDate = moment
          .utc(startDate)
          .add(minDuration, minUnit);
        startDateTime = new Date(calculatedMinDate.toDate());
      } else {
        const getMindate = moment.utc(startDate).add(1, "month");
        startDateTime = new Date(getMindate.toDate());
      }

      const minDateTime = new Date(startDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      minDateTime.setUTCMinutes(minDateTime.getUTCMinutes() - 60);

      setMinEndDate(minDateTime);

      // * calculated max date
      let getMaxdate = null;
      if (applyMaximumDuration && subscriptionMaximumDuration) {
        getMaxdate = moment.utc(startDate).add(maxDuration, maxUnit).toDate();
      } else {
        getMaxdate = moment.utc(startDate).add(10, "years").toDate();
      }

      const limitDateTime = new Date(getMaxdate);
      const maxDateTime = new Date(limitDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      maxDateTime.setUTCMinutes(maxDateTime.getUTCMinutes() - 60);

      setMaxEndDate(maxDateTime);

      const enderDate = moment.utc(finalEndDate).format("YYYY-MM-DD");

      const maxDate = moment.utc(getMaxdate).format("YYYY-MM-DD");

      if (enderDate > maxDate) {
        setEndDate(maxDateTime);
      }

      const isAfter = moment
        .utc(finalEndDate)
        .isBefore(moment.utc(minDateTime));

      if (finalEndDate && startDate) {
        const minDurationDiff = moment
          .utc(finalEndDate)
          .diff(
            moment.utc(startDate),
            applyMinimumDuration ? minUnit : "months",
            true,
          );

        const maxDurationDiff = moment
          .utc(finalEndDate)
          .diff(
            moment.utc(startDate),
            applyMaximumDuration ? maxUnit : "years",
            true,
          );

        const selectedMinDuration = applyMinimumDuration ? minDuration : 1;
        const isMinDurationInvalid = minDurationDiff < selectedMinDuration;

        const selectedMaxDuration = applyMaximumDuration ? maxDuration : 10;
        const isMaxDurationInvalid = maxDurationDiff > selectedMaxDuration;

        if (isMinDurationInvalid) {
          setEndDate(minDateTime);
        }

        if (isMaxDurationInvalid) {
          setEndDate(maxDateTime);
        }
      }

      if (isAfter) {
        setEndDate(minDateTime);
      }
    }
  }, [
    startDate,
    finalEndDate,
    usePersonalizedDates,
    applyMinimumDuration,
    subscriptionMinimumDuration,
    applyMaximumDuration,
    subscriptionMaximumDuration,
  ]);

  useEffect(() => {
    if (!initEndDate) {
      const starterEndData = moment()
        .utcOffset(0, true)
        .add(1, "months")
        .startOf("day")
        .toDate();
      setEndDate(starterEndData);
    }
  }, [initEndDate]);

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Sortie")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              disabled={isDisabled}
              showYearDropdown
              locale={currentLanguage}
              minDate={minEndDate}
              maxDate={maxEndDate}
              selected={endDate}
              onChange={(date: any) => setEndDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={endDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

// UCL Subscription
export const UclSubscriptionEntryDatePicker: FC<any> = ({ getStartDate }) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [startDate, setStartDate] = useState<any>(new Date());

  useEffect(() => {
    getStartDate(startDate, new Date().setHours(0, 0, 0, 0));
    // eslint-disable-next-line
  }, [startDate]);

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Entrée")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              showYearDropdown
              locale={currentLanguage}
              minDate={new Date()}
              selected={startDate}
              onChange={(date: any) => setStartDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={startDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const UclSubscriptionExitDatePicker: FC<any> = ({
  getEndDate,
  startDate,
  finalEndDate,
  subscriptionType,
  applyMinimumDuration,
  subscriptionMinimumDuration,
  applyMaximumDuration,
  subscriptionMaximumDuration,
}) => {
  // ** translation
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [minEndDate, setMinEndDate] = useState<any>(new Date());
  const [endDate, setEndDate] = useState<any>(new Date());
  const [maxEndDate, setMaxEndDate] = useState<any>(new Date());

  useEffect(() => {
    getEndDate(endDate, new Date().setHours(0, 0, 0, 0));
    // eslint-disable-next-line
  }, [endDate]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  useEffect(() => {
    if (subscriptionType === "limited") {
      // * SET MINIMUM DATE TIME
      let startDateTime = null;

      // * current min unit & duration
      const minUnit = subscriptionMinimumDuration?.unit;
      const minDuration = subscriptionMinimumDuration?.value;

      // * current max unit & duration
      const maxUnit = subscriptionMaximumDuration?.unit;
      const maxDuration = subscriptionMaximumDuration?.value;

      // * calculated min datetime
      if (applyMinimumDuration && subscriptionMinimumDuration) {
        const calculatedMinDate = moment
          .utc(startDate)
          .add(minDuration, minUnit);
        startDateTime = new Date(calculatedMinDate.toDate());
      } else {
        const getMindate = moment.utc(startDate).add(15, "day").toDate();
        startDateTime = new Date(getMindate);
      }
      const minDateTime = new Date(startDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      minDateTime.setUTCMinutes(minDateTime.getUTCMinutes() - 60);

      setMinEndDate(minDateTime);

      // * calculated max date
      let getMaxdate = null;
      if (applyMaximumDuration && subscriptionMaximumDuration) {
        getMaxdate = moment.utc(startDate).add(maxDuration, maxUnit).toDate();
      } else {
        getMaxdate = moment.utc(startDate).add(10, "years").toDate();
      }

      const limitDateTime = new Date(getMaxdate);
      const maxDateTime = new Date(limitDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      maxDateTime.setUTCMinutes(maxDateTime.getUTCMinutes() - 60);

      setMaxEndDate(maxDateTime);

      const enderDate = moment.utc(finalEndDate).format("YYYY-MM-DD");
      const maxDate = moment.utc(getMaxdate).format("YYYY-MM-DD");

      if (enderDate > maxDate) {
        setEndDate(maxDateTime);
      }

      const isAfter = moment
        .utc(finalEndDate)
        .isBefore(moment.utc(minDateTime));

      if (finalEndDate && startDate) {
        const minDurationDiff = moment
          .utc(finalEndDate)
          .diff(
            moment.utc(startDate),
            applyMinimumDuration ? minUnit : "days",
            true,
          );

        const maxDurationDiff = moment
          .utc(finalEndDate)
          .diff(
            moment.utc(startDate),
            applyMaximumDuration ? maxUnit : "years",
            true,
          );

        const selectedMinDuration = applyMinimumDuration ? minDuration : 15;
        const isMinDurationInvalid = minDurationDiff < selectedMinDuration;

        const selectedMaxDuration = applyMaximumDuration ? maxDuration : 10;
        const isMaxDurationInvalid = maxDurationDiff > selectedMaxDuration;

        if (isMinDurationInvalid) {
          setEndDate(minDateTime);
        }

        if (isMaxDurationInvalid) {
          setEndDate(maxDateTime);
        }
      }

      if (isAfter) {
        setEndDate(minDateTime);
      }
    }

    if (subscriptionType === "unlimited") {
      const calculatedMaxDate = moment.utc(startDate).add(10, "years").toDate();
      const limitDateTime = new Date(calculatedMaxDate);
      const maxDateTime = new Date(limitDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      maxDateTime.setUTCMinutes(maxDateTime.getUTCMinutes() - 60);
      setEndDate(maxDateTime);
    }
  }, [startDate, finalEndDate, subscriptionType]);

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Sortie")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              disabled={
                subscriptionType === "unlimited" || !subscriptionType
                  ? true
                  : false
              }
              showYearDropdown
              locale={currentLanguage}
              minDate={minEndDate}
              maxDate={maxEndDate}
              selected={endDate}
              onChange={(date: any) => setEndDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={endDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const UclSubscriptionEntryDatePickerUpdate: FC<any> = ({
  getStartDate,
  subStartDate,
  subscriptionType,
}) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  // testing here -1 hour caused by the timezone / check the output in Belguim
  //const minusHour = moment(reservStartDate).subtract({ hours: 1 }).format();

  const starterData = new Date(subStartDate);
  const starterTimeData = new Date(starterData.getTime());
  // it should be + 30 but the time zone keeps on adding 1h
  starterTimeData.setUTCMinutes(starterTimeData.getUTCMinutes() - 60);

  const [startDate, setStartDate] = useState<any>(starterTimeData);

  const currentDate = moment().utcOffset(0, true).startOf("day");
  const disableDataPicker = currentDate.isSameOrAfter(
    moment.utc(subStartDate).startOf("day"),
  );
  useEffect(() => {
    getStartDate(startDate, new Date().setHours(0, 0, 0, 0));
    // eslint-disable-next-line
  }, [startDate]);

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Entrée")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              disabled={disableDataPicker && subscriptionType !== "unlimited"}
              showYearDropdown
              locale={currentLanguage}
              minDate={new Date()}
              selected={startDate}
              onChange={(date: any) => setStartDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={startDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const UclSubscriptionExitDatePickerUpdate: FC<any> = ({
  getEndDate,
  subEndDate,
  startDate,
  finalEndDate,
  subscriptionType,
  applyMinimumDuration,
  subscriptionMinimumDuration,
  applyMaximumDuration,
  subscriptionMaximumDuration,
}) => {
  // ** translation
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const starterEndData = moment.parseZone(subEndDate).startOf("day").toDate();

  // const starterEndData = new Date(subEndDate);
  // const starterTimeData = new Date(starterEndData.getTime());
  // // it should be + 30 but the time zone keeps on adding 1h
  // starterTimeData.setUTCMinutes(starterTimeData.getUTCMinutes() - 60);

  const [minEndDate, setMinEndDate] = useState<any>(new Date());
  const [endDate, setEndDate] = useState<any>(starterEndData);
  const [maxEndDate, setMaxEndDate] = useState<any>(new Date());

  useEffect(() => {
    getEndDate(endDate, new Date().setHours(0, 0, 0, 0));
    // eslint-disable-next-line
  }, [endDate]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  useEffect(() => {
    if (subscriptionType === "limited") {
      // * SET MINIMUM DATE TIME
      let startDateTime = null;

      // * current min unit & duration
      const minUnit = subscriptionMinimumDuration?.unit;
      const minDuration = subscriptionMinimumDuration?.value;

      // * current max unit & duration
      const maxUnit = subscriptionMaximumDuration?.unit;
      const maxDuration = subscriptionMaximumDuration?.value;

      // * calculated min datetime
      if (applyMinimumDuration && subscriptionMinimumDuration) {
        const calculatedMinDate = moment
          .utc(startDate)
          .add(minDuration, minUnit);
        startDateTime = new Date(calculatedMinDate.toDate());
      } else {
        const getMindate = moment.utc(startDate).add(15, "day").toDate();
        startDateTime = new Date(getMindate);
      }
      const minDateTime = new Date(startDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      minDateTime.setUTCMinutes(minDateTime.getUTCMinutes() - 60);

      setMinEndDate(minDateTime);

      // * calculated max date
      let getMaxdate = null;
      if (applyMaximumDuration && subscriptionMaximumDuration) {
        getMaxdate = moment.utc(startDate).add(maxDuration, maxUnit).toDate();
      } else {
        getMaxdate = moment.utc(startDate).add(10, "years").toDate();
      }

      const limitDateTime = new Date(getMaxdate);
      const maxDateTime = new Date(limitDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      maxDateTime.setUTCMinutes(maxDateTime.getUTCMinutes() - 60);

      setMaxEndDate(maxDateTime);

      const enderDate = moment.utc(finalEndDate).format("YYYY-MM-DD");
      const maxDate = moment.utc(getMaxdate).format("YYYY-MM-DD");

      if (enderDate > maxDate) {
        setEndDate(maxDateTime);
      }

      const isAfter = moment
        .utc(finalEndDate)
        .isBefore(moment.utc(minDateTime));

      if (finalEndDate && startDate) {
        const minDurationDiff = moment
          .utc(finalEndDate)
          .diff(
            moment.utc(startDate),
            applyMinimumDuration ? minUnit : "days",
            true,
          );

        const maxDurationDiff = moment
          .utc(finalEndDate)
          .diff(
            moment.utc(startDate),
            applyMaximumDuration ? maxUnit : "years",
            true,
          );

        const selectedMinDuration = applyMinimumDuration ? minDuration : 15;
        const isMinDurationInvalid = minDurationDiff < selectedMinDuration;

        const selectedMaxDuration = applyMaximumDuration ? maxDuration : 10;
        const isMaxDurationInvalid = maxDurationDiff > selectedMaxDuration;

        if (isMinDurationInvalid) {
          setEndDate(minDateTime);
        }

        if (isMaxDurationInvalid) {
          setEndDate(maxDateTime);
        }
      }

      if (isAfter) {
        setEndDate(minDateTime);
      }
    }

    if (subscriptionType === "unlimited") {
      const startDateTime = new Date(startDate);
      const minDateTime = new Date(startDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      minDateTime.setUTCMinutes(minDateTime.getUTCMinutes() - 60);

      setMinEndDate(minDateTime);

      const calculatedMaxDate = moment.utc(startDate).add(10, "years").toDate();
      const limitDateTime = new Date(calculatedMaxDate);
      const maxDateTime = new Date(limitDateTime.getTime());
      // it should be + 30 but the time zone keeps on adding 1h
      maxDateTime.setUTCMinutes(maxDateTime.getUTCMinutes() - 60);
      setEndDate(maxDateTime);
      setMaxEndDate(maxDateTime);
    }
  }, [
    // endDate,
    startDate,
    finalEndDate,
    subscriptionType,
    applyMinimumDuration,
    subscriptionMinimumDuration,
    applyMaximumDuration,
    subscriptionMaximumDuration,
  ]);

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Sortie")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              disabled={subscriptionType === "unlimited" ? true : false}
              showYearDropdown
              locale={currentLanguage}
              minDate={minEndDate}
              maxDate={maxEndDate}
              selected={endDate}
              onChange={(date: any) => setEndDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={endDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

export const PartnerBulkUpdateSubscriptionExitDatePicker: FC<any> = ({
  getStartDate,
}) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [startDate, setStartDate] = useState<any>(new Date());

  useEffect(() => {
    getStartDate(startDate, new Date().setHours(0, 0, 0, 0));
    // eslint-disable-next-line
  }, [startDate]);

  return (
    <Box
      sx={{
        flex: 1,
        border: "1px solid #D9D9D9",
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #D9D9D9",
          p: 1,
          cursor: "pointer",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <Typography sx={{ fontSize: "13px", p: 0, textAlign: "left" }}>
            {t("Sortie")}
          </Typography>
          <DatePickerWrapper>
            <DatePicker
              popperPlacement="right"
              showYearDropdown
              locale={currentLanguage}
              minDate={new Date()}
              selected={startDate}
              onChange={(date: any) => setStartDate(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={startDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

// // ** Dynamic dates
// export const CustomDateTimeInput: FC<any> = forwardRef((props, ref) => {
//   // ** translation
//   const { t, i18n } = useTranslation();
//   const currentLanguage = i18n.language;

//   const startDate = props.start ? moment(props.start) : moment();
//   startDate.locale(currentLanguage);

//   const formattedDate = startDate.format("D MMM YYYY HH:mm");
//   const value = `${formattedDate}`;

//   // const startDate = props.start
//   //   ? moment(props.start).locale(currentLanguage).format("D MMM YYYY")
//   //   : "";

//   // const value = `${startDate}`;
//   return (
//     <>
//       <InputBase
//         sx={{
//           flex: 1,
//           fontSize: "",
//           cursor: "pointer",
//           width: "100%",
//         }}
//         placeholder={t("searchbar")}
//         size="small"
//         inputRef={ref}
//         {...props}
//         value={value}
//       />
//     </>
//   );
// });

export const PartnerDynamicEntryDateTimePicker: FC<any> = ({
  startTime,
  getStartTime,
  rowIdx,
  actionType,
  isDisabled,
}) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [timeStamp, setTimeStamp] = useState<{ date: Date; time: Date }>(() => {
    const currentTime = moment().utcOffset(0, true).seconds(0).milliseconds(0);
    if (!startTime) {
      return { date: new Date(), time: new Date() };
    }

    const initStartTime = moment.utc(startTime);
    const isDatePassed = initStartTime.isBefore(currentTime, "minute");

    if (!isDatePassed || actionType === "UPDATE") {
      const starterData = new Date(startTime);
      const starterTimeData = new Date(starterData.getTime());
      const offsetInMinutes = moment(startTime).utcOffset();

      starterTimeData.setUTCMinutes(
        starterTimeData.getUTCMinutes() - offsetInMinutes,
      );

      return { date: starterTimeData, time: starterTimeData };
    } else {
      // Always return a date
      return { date: new Date(), time: new Date() };
    }
  });

  const handleDateChange = (date: any) => {
    setTimeStamp((prevData: any) => ({ ...prevData, date }));
  };

  const handleTimeChange = (time: any) => {
    setTimeStamp((prevData: any) => ({ ...prevData, time }));
  };

  useEffect(() => {
    getStartTime(timeStamp.date, timeStamp.time, "startTime", rowIdx);
    // eslint-disable-next-line
  }, [timeStamp.date, timeStamp.time, rowIdx]);

  // ** Disable previous hours => start from current time until the end of the day (minTime/maxTime)
  const minTime = moment().toDate();
  if (moment(timeStamp.date).isSame(moment(), "day")) {
    minTime.setHours(moment().hours());
    minTime.setMinutes(moment().minutes());
  } else {
    minTime.setHours(0);
    minTime.setMinutes(0);
  }

  const endOfDay = moment(new Date()).endOf("day");
  const endOfDayAsDate = endOfDay.toDate();

  useEffect(() => {
    // ? NO INIT VALUE EXIST
    if (!startTime) {
      setTimeStamp({ date: new Date(), time: new Date() });
      return;
    }

    if (startTime && actionType) {
      const currentTime = moment()
        .utcOffset(0, true)
        .seconds(0)
        .milliseconds(0);
      const initStartTime = moment.utc(startTime);
      const isDatePassed = initStartTime.isBefore(currentTime, "minute");

      if (!isDatePassed || actionType === "UPDATE") {
        const starterData = new Date(startTime);
        const starterTimeData = new Date(starterData.getTime());
        const offsetInMinutes = moment(startTime).utcOffset();

        starterTimeData.setUTCMinutes(
          starterTimeData.getUTCMinutes() - offsetInMinutes,
        );
        setTimeStamp({ date: starterTimeData, time: starterTimeData });
        return;
      } else {
        // Always return a date
        setTimeStamp({ date: new Date(), time: new Date() });
        return;
      }
    }
  }, [startTime, actionType]);

  const currentTime = moment().utcOffset(0, true).seconds(0).milliseconds(0);
  const selectedStartTime = moment.utc(startTime);
  const disableDatePicker =
    actionType === "UPDATE" && currentTime.isSameOrAfter(selectedStartTime);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        width: "100%",
        justifyContent: "center",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          cursor: "pointer",
          p: 1,
        }}>
        <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div style={{ display: "flex", flexDirection: "row" }}>
          <DatePickerWrapper>
            <DatePicker
              disabled={disableDatePicker || isDisabled}
              showYearDropdown
              locale={currentLanguage}
              minDate={new Date()}
              selected={timeStamp.date}
              onChange={(date: any) => handleDateChange(date)}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={timeStamp.date}
                />
              }
            />
          </DatePickerWrapper>
          <TimePickerWrapper sx={{ marginLeft: -5 }}>
            <DatePicker
              disabled={disableDatePicker || isDisabled}
              locale={currentLanguage}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={5}
              dateFormat={currentLanguage === "en" ? "HH:mm" : "hh:mm"}
              timeFormat="HH:mm"
              selected={timeStamp.time}
              onChange={handleTimeChange}
              minTime={minTime}
              maxTime={endOfDayAsDate}
              customInput={
                <CustomInputTime
                  label={t("Sélectionner une date")}
                  start={timeStamp.time}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

// export const PartnerDynamicExitDateTimePicker: FC<any> = ({
//   startTime,
//   endTime,
//   getEndTime,
//   pmsType,
//   ticketType,
//   rowIdx,
//   isDisabled,
//   applyMinimumDuration,
//   reservationMinimumDuration,
//   applyMaximumDuration,
//   reservationMaximumDuration,
// }) => {
//   // ** translation
//   const { t, i18n } = useTranslation();
//   const currentLanguage = i18n.language;

//   registerLocale(
//     currentLanguage,
//     currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
//   );

//   const SKIDATA_MAX_MINUTES = 89280; // 62 days
//   const DEFAULT_MAX_MINUTES = 5259600; // 10 years

//   const maxMinutes =
//     pmsType === "Skidata" &&
//     (ticketType === "Single Entry Reservation" ||
//       ticketType === "Réservation hockey")
//       ? SKIDATA_MAX_MINUTES
//       : DEFAULT_MAX_MINUTES;

//   // * current min unit & duration
//   const minUnit = reservationMinimumDuration?.unit;
//   const minDuration = reservationMinimumDuration?.value;

//   // * current max unit & duration
//   const maxUnit = reservationMaximumDuration?.unit;
//   const maxDuration = reservationMaximumDuration?.value;

//   const calculateMinDateTime = ({
//     date,
//     duration,
//     unit,
//   }: {
//     date: Date | string;
//     duration: number;
//     unit: "minutes" | "hours" | "days" | "weeks" | "months";
//   }): { date: Date; time: Date } => {
//     const dateTime = moment.utc(date).add(duration, unit);

//     const selectedDate = new Date(dateTime.toDate());
//     const selectedTimeData = new Date(selectedDate.getTime());
//     const offsetInMinutes = moment(dateTime.toDate()).utcOffset();

//     selectedTimeData.setUTCMinutes(
//       selectedTimeData.getUTCMinutes() - offsetInMinutes,
//     );

//     return { date: selectedTimeData, time: selectedTimeData };
//   };

//   const defaultMinTimeStamp = applyMinimumDuration
//     ? calculateMinDateTime({
//         date: new Date(),
//         unit: minUnit,
//         duration: minDuration,
//       })
//     : { date: new Date(), time: new Date() };

//   const [timeStamp, setTimeStamp] = useState<{ date: Date; time: Date }>(() => {
//     const currentTime = moment().utcOffset(0, true).seconds(0).milliseconds(0);
//     if (!endTime) {
//       return {
//         date: defaultMinTimeStamp?.date,
//         time: defaultMinTimeStamp?.time,
//       };
//     }

//     let minDateTime = null;
//     if (applyMinimumDuration) {
//       const { date, time } = calculateMinDateTime({
//         date: startTime,
//         duration: minDuration,
//         unit: minUnit,
//       });

//       minDateTime = moment(date).utcOffset(0, true);
//     } else {
//       const starterData = new Date(startTime);
//       const starterTimeData = new Date(starterData.getTime());
//       const offsetInMinutes = moment(startTime).utcOffset();

//       starterTimeData.setUTCMinutes(
//         starterTimeData.getUTCMinutes() - offsetInMinutes,
//       );
//       minDateTime = moment(starterTimeData).utcOffset(0, true);
//     }

//     const initEndTime = moment.utc(endTime);
//     const isDatePassed = initEndTime.isBefore(currentTime);
//     const isEndDateInvalid = initEndTime.isBefore(minDateTime);

//     if (!isDatePassed) {
//       if (!isEndDateInvalid) {
//         const selectedDate = new Date(endTime);
//         const selectedTimeData = new Date(selectedDate.getTime());
//         const offsetInMinutes = moment(endTime).utcOffset();

//         selectedTimeData.setUTCMinutes(
//           selectedTimeData.getUTCMinutes() - offsetInMinutes,
//         );

//         return { date: selectedTimeData, time: selectedTimeData };
//       } else {
//         const selectedDate = new Date(minDateTime.toDate());
//         const selectedTimeData = new Date(selectedDate.getTime());
//         const offsetInMinutes = moment(minDateTime.toISOString()).utcOffset();

//         selectedTimeData.setUTCMinutes(
//           selectedTimeData.getUTCMinutes() - offsetInMinutes,
//         );

//         return { date: selectedTimeData, time: selectedTimeData };
//       }
//     } else {
//       // Always return a date
//       return {
//         date: defaultMinTimeStamp?.date,
//         time: defaultMinTimeStamp?.time,
//       };
//     }
//   });

//   const [minTimeStamp, setMinTimeStamp] = useState<{ date: Date; time: Date }>(
//     () => {
//       if (!startTime) {
//         return {
//           date: defaultMinTimeStamp?.date,
//           time: defaultMinTimeStamp?.time,
//         };
//       }

//       if (applyMinimumDuration) {
//         const { date, time } = calculateMinDateTime({
//           date: startTime,
//           duration: minDuration,
//           unit: minUnit,
//         });
//         return { date, time };
//       } else {
//         const starterData = new Date(startTime);
//         const starterTimeData = new Date(starterData.getTime());
//         const offsetInMinutes = moment(startTime).utcOffset();

//         starterTimeData.setUTCMinutes(
//           starterTimeData.getUTCMinutes() - offsetInMinutes,
//         );

//         return { date: starterTimeData, time: starterTimeData };
//       }
//     },
//   );

//   const [maxTimeStamp, setMaxTimeStamp] = useState<{ date: Date; time: Date }>(
//     () => {
//       if (!startTime) {
//         const calculatedMaxDate = moment()
//           .utcOffset(0, true)
//           .add(
//             applyMaximumDuration ? maxDuration : maxMinutes,
//             applyMaximumDuration ? maxUnit : "minutes",
//           )
//           .toISOString();
//         const maxDate = new Date(calculatedMaxDate);
//         const maxTimeData = new Date(maxDate.getTime());
//         const offsetInMinutes = moment(maxDate).utcOffset();

//         maxTimeData.setUTCMinutes(
//           maxTimeData.getUTCMinutes() - offsetInMinutes,
//         );
//         return { date: maxTimeData, time: maxTimeData };
//       }

//       const calculatedMaxDate = moment
//         .utc(startTime)
//         .add(
//           applyMaximumDuration ? maxDuration : maxMinutes,
//           applyMaximumDuration ? maxUnit : "minutes",
//         )
//         .toISOString();
//       const maxDate = new Date(calculatedMaxDate);
//       const maxTimeData = new Date(maxDate.getTime());
//       const offsetInMinutes = moment(maxDate).utcOffset();

//       maxTimeData.setUTCMinutes(maxTimeData.getUTCMinutes() - offsetInMinutes);
//       return { date: maxTimeData, time: maxTimeData };
//     },
//   );

//   const [showFullTime, setShowFullTime] = useState(false);
//   const [showEndOfDay, setShowEndOfDay] = useState(false);

//   // * UPDATE END TIME
//   useEffect(() => {
//     getEndTime(timeStamp.date, timeStamp.time, "endTime", rowIdx);
//     // eslint-disable-next-line
//   }, [timeStamp.date, timeStamp.time, rowIdx]);

//   // * DATE & TIME CONSTRAINTS
//   useEffect(() => {
//     if (!startTime) {
//       // ? min contraint
//       setMinTimeStamp({
//         date: defaultMinTimeStamp?.date,
//         time: defaultMinTimeStamp?.time,
//       });
//       // ? max contraint
//       const calculatedMaxDate = moment()
//         .utcOffset(0, true)
//         .add(
//           applyMaximumDuration ? maxDuration : maxMinutes,
//           applyMaximumDuration ? maxUnit : "minutes",
//         )
//         .toISOString();
//       const maxDate = new Date(calculatedMaxDate);
//       const maxTimeData = new Date(maxDate.getTime());
//       const offsetInMinutes = moment(maxDate).utcOffset();

//       maxTimeData.setUTCMinutes(maxTimeData.getUTCMinutes() - offsetInMinutes);
//       setMaxTimeStamp({ date: maxTimeData, time: maxTimeData });

//       return;
//     }

//     // ! min constraint
//     if (applyMinimumDuration) {
//       const { date, time } = calculateMinDateTime({
//         date: startTime,
//         duration: minDuration,
//         unit: minUnit,
//       });
//       setMinTimeStamp({ date, time });
//     } else {
//       // ? offset in min
//       const minOffsetInMinutes = moment(startTime).utcOffset();
//       // ? min contraint
//       const starterData = new Date(startTime);
//       const starterTimeData = new Date(starterData.getTime());
//       starterTimeData.setUTCMinutes(
//         starterTimeData.getUTCMinutes() - minOffsetInMinutes,
//       );
//       setMinTimeStamp({ date: starterTimeData, time: starterTimeData });
//     }

//     // ! max contraint
//     const calculatedMaxDate = moment
//       .utc(startTime)
//       .add(
//         applyMaximumDuration ? maxDuration : maxMinutes,
//         applyMaximumDuration ? maxUnit : "minutes",
//       )
//       .toISOString();
//     const maxDate = new Date(calculatedMaxDate);
//     const maxOffsetInMinutes = moment(maxDate).utcOffset();
//     const maxTimeData = new Date(maxDate.getTime());
//     maxTimeData.setUTCMinutes(maxTimeData.getUTCMinutes() - maxOffsetInMinutes);
//     setMaxTimeStamp({ date: maxTimeData, time: maxTimeData });
//     return;
//   }, [startTime, maxMinutes, applyMinimumDuration, applyMaximumDuration]);

//   // * END TIME
//   useEffect(() => {
//     if (!endTime) {
//       if (startTime) {
//         if (applyMinimumDuration) {
//           const { date, time } = calculateMinDateTime({
//             date: startTime,
//             duration: minDuration,
//             unit: minUnit,
//           });
//           setTimeStamp({ date, time });
//           return;
//         } else {
//           const starterData = new Date(startTime);
//           const starterTimeData = new Date(starterData.getTime());
//           const offsetInMinutes = moment(startTime).utcOffset();
//           starterTimeData.setUTCMinutes(
//             starterTimeData.getUTCMinutes() - offsetInMinutes,
//           );
//           setTimeStamp({ date: starterTimeData, time: starterTimeData });
//           return;
//         }
//       } else if (!startTime) {
//         setTimeStamp({
//           date: defaultMinTimeStamp?.date,
//           time: defaultMinTimeStamp?.time,
//         });
//         return;
//       }
//     }

//     const currentTime = moment().utcOffset(0, true);
//     const selectedStartTime = moment.utc(startTime);
//     const selectedEndTime = moment.utc(endTime);

//     const maxDate = moment.utc(maxTimeStamp.date);
//     const offsetInMinutes = moment(maxDate).utcOffset();

//     if (selectedEndTime.isAfter(maxDate, "minute")) {
//       const maxDate = new Date(maxTimeStamp.date);
//       const maxTimeData = new Date(maxDate.getTime());
//       maxTimeData.setUTCMinutes(maxTimeData.getUTCMinutes() - offsetInMinutes);
//       setTimeStamp({ date: maxTimeData, time: maxTimeData });
//       setShowFullTime(true);
//       setShowEndOfDay(false);
//       return;
//     }

//     if (maxDate.isAfter(selectedEndTime)) {
//       setShowFullTime(true);
//       setShowEndOfDay(true);
//     }

//     if (selectedEndTime.isSame(maxDate, "days")) {
//       setShowFullTime(true);
//       setShowEndOfDay(false);
//     }

//     if (selectedStartTime.isAfter(selectedEndTime)) {
//       const starterData = new Date(startTime);
//       const starterTimeData = new Date(starterData.getTime());
//       const offsetInMinutes = moment(starterData).utcOffset();
//       starterTimeData.setUTCMinutes(
//         starterTimeData.getUTCMinutes() - offsetInMinutes,
//       );
//       setTimeStamp({ date: starterTimeData, time: starterTimeData });
//       return;
//     }

//     if (
//       selectedStartTime.isSame(currentTime, "days") &&
//       selectedEndTime.isSame(currentTime, "days")
//     ) {
//       setShowFullTime(false);
//       setShowEndOfDay(true);
//     }

//     if (selectedEndTime.isAfter(selectedStartTime, "days")) {
//       setShowFullTime(true);
//       setShowEndOfDay(true);
//     }
//   }, [startTime, endTime, maxTimeStamp, applyMinimumDuration]);

//   // * HANDLE DATE & TIME CHANGES
//   const handleDateChange = (date: any) => {
//     setTimeStamp((prevData: any) => ({ ...prevData, date }));
//   };

//   const handleTimeChange = (time: any) => {
//     setTimeStamp((prevData: any) => ({ ...prevData, time }));
//   };

//   const endOfDay = moment(new Date()).endOf("day");
//   const endOfDayAsDate = endOfDay.toDate();

//   // console.log({
//   //   startTime,
//   //   endTime,
//   //   getEndTime,
//   //   pmsType,
//   //   ticketType,
//   //   rowIdx,
//   //   isDisabled,
//   //   applyMinimumDuration,
//   //   reservationMinimumDuration,
//   //   applyMaximumDuration,
//   //   reservationMaximumDuration,
//   //   timeStampDate: moment(timeStamp.date).utcOffset(0, true).toISOString(),
//   //   timeStampTime: moment(timeStamp.time).utcOffset(0, true).toISOString(),
//   //   minTimeStampDate: moment(minTimeStamp.date)
//   //     .utcOffset(0, true)
//   //     .toISOString(),
//   //   minTimeStampTime: moment(minTimeStamp.time)
//   //     .utcOffset(0, true)
//   //     .toISOString(),
//   //   maxTimeStampDate: moment(maxTimeStamp.date)
//   //     .utcOffset(0, true)
//   //     .toISOString(),
//   //   maxTimeStampTime: moment(maxTimeStamp.time)
//   //     .utcOffset(0, true)
//   //     .toISOString(),
//   // });

//   return (
//     <Box
//       sx={{
//         flex: 1,
//         display: "flex",
//         width: "100%",
//         justifyContent: "center",
//       }}>
//       <Box
//         sx={{
//           flex: 1,
//           display: "flex",
//           justifyContent: "center",
//           cursor: "pointer",
//           p: 1,
//         }}>
//         <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
//         <div style={{ display: "flex", flexDirection: "row" }}>
//           <DatePickerWrapper>
//             <DatePicker
//               disabled={isDisabled}
//               showYearDropdown
//               locale={currentLanguage}
//               minDate={minTimeStamp.date}
//               maxDate={maxTimeStamp.date}
//               selected={timeStamp.date}
//               onChange={(date: any) => handleDateChange(date)}
//               customInput={
//                 <CustomDateInput
//                   label={t("Sélectionner une date")}
//                   start={timeStamp.date}
//                 />
//               }
//             />
//           </DatePickerWrapper>

//           <TimePickerWrapper sx={{ marginLeft: -5 }}>
//             <DatePicker
//               disabled={isDisabled}
//               locale={currentLanguage}
//               showTimeSelect
//               showTimeSelectOnly
//               timeIntervals={5}
//               dateFormat={currentLanguage === "en" ? "HH:mm" : "hh:mm"}
//               timeFormat="HH:mm"
//               selected={timeStamp.time}
//               onChange={handleTimeChange}
//               minTime={
//                 showFullTime
//                   ? new Date(new Date().setHours(0, 0, 0, 0))
//                   : minTimeStamp.time
//               }
//               maxTime={showEndOfDay ? endOfDayAsDate : minTimeStamp.time} // sets maximum time to end of the day
//               customInput={
//                 <CustomInputTime
//                   label={t("Sélectionner une date")}
//                   start={timeStamp.time}
//                 />
//               }
//             />
//           </TimePickerWrapper>
//         </div>
//       </Box>
//     </Box>
//   );
// };

// export const PartnerDynamicExitDateTimePicker: FC<any> = ({
//   startTime,
//   endTime,
//   getEndTime,
//   pmsType,
//   ticketType,
//   rowIdx,
//   isDisabled,
//   applyMinimumDuration,
//   reservationMinimumDuration,
//   applyMaximumDuration,
//   reservationMaximumDuration,
// }) => {
//   // ** translation
//   const { t, i18n } = useTranslation();
//   const currentLanguage = i18n.language;

//   registerLocale(
//     currentLanguage,
//     currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
//   );

//   const SKIDATA_MAX_MINUTES = 89280; // 62 days
//   const DEFAULT_MAX_MINUTES = 5259600; // 10 years

//   const maxMinutes =
//     pmsType === "Skidata" &&
//     (ticketType === "Single Entry Reservation" ||
//       ticketType === "Réservation hockey")
//       ? SKIDATA_MAX_MINUTES
//       : DEFAULT_MAX_MINUTES;

//     // * current min unit & duration
//     const minUnit = reservationMinimumDuration?.unit;
//     const minDuration = reservationMinimumDuration?.value;

//     // * current max unit & duration
//     const maxUnit = reservationMaximumDuration?.unit;
//     const maxDuration = reservationMaximumDuration?.value;

//   const [timeStamp, setTimeStamp] = useState<{ date: Date; time: Date }>(() => {
//     const currentTime = moment().utcOffset(0, true).seconds(0).milliseconds(0);
//     if (!endTime) {
//       return { date: new Date(), time: new Date() };
//     }

//     const initEndTime = moment.utc(endTime);
//     const isDatePassed = initEndTime.isBefore(currentTime);

//     if (!isDatePassed) {
//       const selectedDate = new Date(endTime);
//       const selectedTimeData = new Date(selectedDate.getTime());
//       const offsetInMinutes = moment(endTime).utcOffset();

//       selectedTimeData.setUTCMinutes(
//         selectedTimeData.getUTCMinutes() - offsetInMinutes,
//       );

//       return { date: selectedTimeData, time: selectedTimeData };
//     } else {
//       // Always return a date
//       return { date: new Date(), time: new Date() };
//     }
//   });

//   const [minTimeStamp, setMinTimeStamp] = useState<{ date: Date; time: Date }>(
//     () => {
//       if (!startTime) {
//         return { date: new Date(), time: new Date() };
//       }

//       const starterData = new Date(startTime);
//       const starterTimeData = new Date(starterData.getTime());
//       const offsetInMinutes = moment(startTime).utcOffset();

//       starterTimeData.setUTCMinutes(
//         starterTimeData.getUTCMinutes() - offsetInMinutes,
//       );

//       return { date: starterTimeData, time: starterTimeData };
//     },
//   );

//   const [maxTimeStamp, setMaxTimeStamp] = useState<{ date: Date; time: Date }>(
//     () => {
//       if (!startTime) {
//         const calculatedMaxDate = moment()
//           .utcOffset(0, true)
//           .add(maxMinutes, "minutes")
//           .toISOString();
//         const maxDate = new Date(calculatedMaxDate);
//         const maxTimeData = new Date(maxDate.getTime());
//         const offsetInMinutes = moment(maxDate).utcOffset();

//         maxTimeData.setUTCMinutes(
//           maxTimeData.getUTCMinutes() - offsetInMinutes,
//         );
//         return { date: maxTimeData, time: maxTimeData };
//       }

//       const calculatedMaxDate = moment
//         .utc(startTime)
//         .add(maxMinutes, "minutes")
//         .toISOString();
//       const maxDate = new Date(calculatedMaxDate);
//       const maxTimeData = new Date(maxDate.getTime());
//       const offsetInMinutes = moment(maxDate).utcOffset();

//       maxTimeData.setUTCMinutes(maxTimeData.getUTCMinutes() - offsetInMinutes);
//       return { date: maxTimeData, time: maxTimeData };
//     },
//   );

//   const [showFullTime, setShowFullTime] = useState(false);
//   const [showEndOfDay, setShowEndOfDay] = useState(false);

//   // * UPDATE END TIME
//   useEffect(() => {
//     getEndTime(timeStamp.date, timeStamp.time, "endTime", rowIdx);
//     // eslint-disable-next-line
//   }, [timeStamp.date, timeStamp.time, rowIdx]);

//   // * DATE & TIME CONSTRAINTS
//   useEffect(() => {
//     if (!startTime) {
//       // ? min contraint
//       setMinTimeStamp({ date: new Date(), time: new Date() });
//       // ? max contraint
//       const calculatedMaxDate = moment()
//         .utcOffset(0, true)
//         .add(maxMinutes, "minutes")
//         .toISOString();
//       const maxDate = new Date(calculatedMaxDate);
//       const maxTimeData = new Date(maxDate.getTime());
//       const offsetInMinutes = moment(maxDate).utcOffset();

//       maxTimeData.setUTCMinutes(maxTimeData.getUTCMinutes() - offsetInMinutes);
//       setMaxTimeStamp({ date: maxTimeData, time: maxTimeData });

//       return;
//     }

//     // ? offset in min
//     const minOffsetInMinutes = moment(startTime).utcOffset();
//     // ? min contraint
//     const starterData = new Date(startTime);
//     const starterTimeData = new Date(starterData.getTime());
//     starterTimeData.setUTCMinutes(
//       starterTimeData.getUTCMinutes() - minOffsetInMinutes,
//     );
//     setMinTimeStamp({ date: starterTimeData, time: starterTimeData });

//     // ? max contraint
//     const calculatedMaxDate = moment
//       .utc(startTime)
//       .add(maxMinutes, "minutes")
//       .toISOString();
//     const maxDate = new Date(calculatedMaxDate);
//     const maxOffsetInMinutes = moment(maxDate).utcOffset();
//     const maxTimeData = new Date(maxDate.getTime());
//     maxTimeData.setUTCMinutes(maxTimeData.getUTCMinutes() - maxOffsetInMinutes);
//     setMaxTimeStamp({ date: maxTimeData, time: maxTimeData });
//     return;
//   }, [startTime, maxMinutes]);

//   // * END TIME
//   useEffect(() => {
//     if (!endTime) {
//       if (startTime) {
//         const starterData = new Date(startTime);
//         const starterTimeData = new Date(starterData.getTime());
//         const offsetInMinutes = moment(startTime).utcOffset();
//         starterTimeData.setUTCMinutes(
//           starterTimeData.getUTCMinutes() - offsetInMinutes,
//         );
//         setTimeStamp({ date: starterTimeData, time: starterTimeData });
//         return;
//       } else {
//         setTimeStamp({ date: new Date(), time: new Date() });
//         return;
//       }
//     }

//     const currentTime = moment().utcOffset(0, true);
//     const selectedStartTime = moment.utc(startTime);
//     const selectedEndTime = moment.utc(endTime);

//     const maxDate = moment.utc(maxTimeStamp.date);
//     const offsetInMinutes = moment(maxDate).utcOffset();

//     if (selectedEndTime.isAfter(maxDate, "minute")) {
//       const maxDate = new Date(maxTimeStamp.date);
//       const maxTimeData = new Date(maxDate.getTime());
//       maxTimeData.setUTCMinutes(maxTimeData.getUTCMinutes() - offsetInMinutes);
//       setTimeStamp({ date: maxTimeData, time: maxTimeData });
//       setShowFullTime(true);
//       setShowEndOfDay(false);
//       return;
//     }

//     if (maxDate.isAfter(selectedEndTime)) {
//       setShowFullTime(true);
//       setShowEndOfDay(true);
//     }

//     if (selectedEndTime.isSame(maxDate, "days")) {
//       setShowFullTime(true);
//       setShowEndOfDay(false);
//     }

//     if (selectedStartTime.isAfter(selectedEndTime)) {
//       const starterData = new Date(startTime);
//       const starterTimeData = new Date(starterData.getTime());
//       const offsetInMinutes = moment(starterData).utcOffset();
//       starterTimeData.setUTCMinutes(
//         starterTimeData.getUTCMinutes() - offsetInMinutes,
//       );
//       setTimeStamp({ date: starterTimeData, time: starterTimeData });
//       return;
//     }

//     if (
//       selectedStartTime.isSame(currentTime, "days") &&
//       selectedEndTime.isSame(currentTime, "days")
//     ) {
//       setShowFullTime(false);
//       setShowEndOfDay(true);
//     }

//     if (selectedEndTime.isAfter(selectedStartTime, "days")) {
//       setShowFullTime(true);
//       setShowEndOfDay(true);
//     }
//   }, [startTime, endTime, maxTimeStamp]);

//   // * HANDLE DATE & TIME CHANGES
//   const handleDateChange = (date: any) => {
//     setTimeStamp((prevData: any) => ({ ...prevData, date }));
//   };

//   const handleTimeChange = (time: any) => {
//     setTimeStamp((prevData: any) => ({ ...prevData, time }));
//   };

//   const endOfDay = moment(new Date()).endOf("day");
//   const endOfDayAsDate = endOfDay.toDate();

//   return (
//     <Box
//       sx={{
//         flex: 1,
//         display: "flex",
//         width: "100%",
//         justifyContent: "center",
//       }}>
//       <Box
//         sx={{
//           flex: 1,
//           display: "flex",
//           justifyContent: "center",
//           cursor: "pointer",
//           p: 1,
//         }}>
//         <ClockOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
//         <div style={{ display: "flex", flexDirection: "row" }}>
//           <DatePickerWrapper>
//             <DatePicker
//               disabled={isDisabled}
//               showYearDropdown
//               locale={currentLanguage}
//               minDate={minTimeStamp.date}
//               maxDate={maxTimeStamp.date}
//               selected={timeStamp.date}
//               onChange={(date: any) => handleDateChange(date)}
//               customInput={
//                 <CustomDateInput
//                   label={t("Sélectionner une date")}
//                   start={timeStamp.date}
//                 />
//               }
//             />
//           </DatePickerWrapper>

//           <TimePickerWrapper sx={{ marginLeft: -5 }}>
//             <DatePicker
//               disabled={isDisabled}
//               locale={currentLanguage}
//               showTimeSelect
//               showTimeSelectOnly
//               timeIntervals={5}
//               dateFormat={currentLanguage === "en" ? "HH:mm" : "hh:mm"}
//               timeFormat="HH:mm"
//               selected={timeStamp.time}
//               onChange={handleTimeChange}
//               minTime={
//                 showFullTime
//                   ? new Date(new Date().setHours(0, 0, 0, 0))
//                   : minTimeStamp.time
//               }
//               maxTime={showEndOfDay ? endOfDayAsDate : minTimeStamp.time} // sets maximum time to end of the day
//               customInput={
//                 <CustomInputTime
//                   label={t("Sélectionner une date")}
//                   start={timeStamp.time}
//                 />
//               }
//             />
//           </TimePickerWrapper>
//         </div>
//       </Box>
//     </Box>
//   );
// };

export const PartnerDynamicExitDateTimePicker: FC<any> = ({
  startTime,
  endTime,
  getEndTime,
  pmsType,
  ticketType,
  rowIdx,
  isDisabled,
  applyMinimumDuration,
  reservationMinimumDuration,
  applyMaximumDuration,
  reservationMaximumDuration,
}) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  /**
   * ---------------------------------------------------------
   * PMS LIMITS
   * ---------------------------------------------------------
   */

  const SKIDATA_MAX_MINUTES = 89280; // 62 days
  const DEFAULT_MAX_MINUTES = 5259600; // 10 years

  const maxMinutes =
    pmsType === "Skidata" &&
    (ticketType === "Single Entry Reservation" ||
      ticketType === "Réservation hockey")
      ? SKIDATA_MAX_MINUTES
      : DEFAULT_MAX_MINUTES;

  /**
   * ---------------------------------------------------------
   * DURATION CONFIGURATION
   * ---------------------------------------------------------
   */

  const minUnit = reservationMinimumDuration?.unit;
  const minDuration = reservationMinimumDuration?.value;

  const maxUnit = reservationMaximumDuration?.unit;
  const maxDuration = reservationMaximumDuration?.value;

  /**
   * ---------------------------------------------------------
   * CONVERT DURATION TO MINUTES
   * ---------------------------------------------------------
   */

  const durationToMinutes = (value?: number, unit?: string): number => {
    if (!value || !unit) {
      return 0;
    }

    switch (unit) {
      case "minutes":
        return Number(value);

      case "hours":
        return Number(value) * 60;

      case "days":
        return Number(value) * 24 * 60;

      default:
        return 0;
    }
  };

  const minimumDurationMinutes =
    applyMinimumDuration &&
    ["Single Entry Reservation", "Réservation hockey"].includes(ticketType)
      ? durationToMinutes(minDuration, minUnit)
      : 0;

  const configuredMaximumDurationMinutes = applyMaximumDuration
    ? durationToMinutes(maxDuration, maxUnit)
    : 0;

  /**
   * ---------------------------------------------------------
   * CONVERT MOMENT DATE TO DATEPICKER DATE
   *
   * This is the important timezone correction.
   *
   * Example:
   *
   * maxMoment.toDate()
   *       ↓
   * convertToPickerDate()
   *       ↓
   * Date used by react-datepicker
   *
   * It follows the same principle already used in your
   * original component.
   * ---------------------------------------------------------
   */

  const convertToPickerDate = (date: Date): Date => {
    const selectedDate = new Date(date);
    const selectedTimeData = new Date(selectedDate.getTime());

    const offsetInMinutes = moment(selectedDate).utcOffset();

    selectedTimeData.setUTCMinutes(
      selectedTimeData.getUTCMinutes() - offsetInMinutes,
    );

    return selectedTimeData;
  };

  /**
   * ---------------------------------------------------------
   * CONVERT PICKER DATE TO UTC MOMENT
   * ---------------------------------------------------------
   */

  const pickerDateToMoment = (date: Date): moment.Moment => {
    return moment.utc({
      year: date.getFullYear(),
      month: date.getMonth(),
      date: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
      millisecond: 0,
    });
  };

  /**
   * ---------------------------------------------------------
   * BACKEND DATE -> PICKER DATE
   * ---------------------------------------------------------
   */

  const toPickerDate = (value?: string | Date | null): Date | null => {
    if (!value) {
      return null;
    }

    const date = new Date(value);

    return convertToPickerDate(date);
  };

  /**
   * ---------------------------------------------------------
   * CLAMP DATE
   * ---------------------------------------------------------
   */

  const clampDate = (date: Date, minDate: Date, maxDate: Date): Date => {
    if (date.getTime() < minDate.getTime()) {
      return new Date(minDate);
    }

    if (date.getTime() > maxDate.getTime()) {
      return new Date(maxDate);
    }

    return new Date(date);
  };

  /**
   * ---------------------------------------------------------
   * MIN / MAX TIMESTAMP
   * ---------------------------------------------------------
   */

  const [minTimeStamp, setMinTimeStamp] = useState<{
    date: Date;
    time: Date;
  }>(() => {
    if (!startTime) {
      const now = new Date();

      return {
        date: now,
        time: now,
      };
    }

    const startDate = toPickerDate(startTime);

    if (!startDate) {
      const now = new Date();

      return {
        date: now,
        time: now,
      };
    }

    const startMoment = pickerDateToMoment(startDate);

    const minMoment = startMoment
      .clone()
      .add(minimumDurationMinutes, "minutes");

    const minDate = convertToPickerDate(minMoment.toDate());

    return {
      date: minDate,
      time: minDate,
    };
  });

  const [maxTimeStamp, setMaxTimeStamp] = useState<{
    date: Date;
    time: Date;
  }>(() => {
    if (!startTime) {
      const now = new Date();

      const maxMoment = pickerDateToMoment(now).add(maxMinutes, "minutes");

      const maxDate = convertToPickerDate(maxMoment.toDate());

      return {
        date: maxDate,
        time: maxDate,
      };
    }

    const startDate = toPickerDate(startTime);

    if (!startDate) {
      const now = new Date();

      return {
        date: now,
        time: now,
      };
    }

    const startMoment = pickerDateToMoment(startDate);

    const effectiveMaximumMinutes =
      applyMaximumDuration &&
      ["Single Entry Reservation", "Réservation hockey"].includes(ticketType)
        ? configuredMaximumDurationMinutes
        : maxMinutes;

    /**
     * Start + maximum duration
     */
    const maxMoment = startMoment
      .clone()
      .add(effectiveMaximumMinutes, "minutes");

    /**
     * IMPORTANT:
     * Apply timezone correction here.
     */
    const maxDate = convertToPickerDate(maxMoment.toDate());

    return {
      date: maxDate,
      time: maxDate,
    };
  });

  /**
   * ---------------------------------------------------------
   * SELECTED END TIME
   * ---------------------------------------------------------
   */

  const [timeStamp, setTimeStamp] = useState<{
    date: Date;
    time: Date;
  }>(() => {
    const minDate = minTimeStamp.date;
    const maxDate = maxTimeStamp.date;

    /**
     * Existing end time
     */
    const existingEndTime = toPickerDate(endTime);

    if (existingEndTime) {
      const validEndTime = clampDate(existingEndTime, minDate, maxDate);

      return {
        date: validEndTime,
        time: validEndTime,
      };
    }

    /**
     * No end time but start time exists:
     * initialize with minimum allowed duration.
     */
    if (startTime) {
      return {
        date: new Date(minDate),
        time: new Date(minDate),
      };
    }

    /**
     * No start time
     */
    const now = new Date();

    return {
      date: now,
      time: now,
    };
  });

  const [showFullTime, setShowFullTime] = useState(false);
  const [showEndOfDay, setShowEndOfDay] = useState(false);

  /**
   * ---------------------------------------------------------
   * CALCULATE MINIMUM / MAXIMUM DATES
   * ---------------------------------------------------------
   */

  useEffect(() => {
    /**
     * -------------------------------------------------------
     * NO START TIME
     * -------------------------------------------------------
     */

    if (!startTime) {
      const now = new Date();

      /**
       * No start time means we cannot calculate a configured
       * reservation duration.
       *
       * Use current time as minimum.
       */
      const maxMoment = pickerDateToMoment(now).add(maxMinutes, "minutes");

      const maxDate = convertToPickerDate(maxMoment.toDate());

      setMinTimeStamp({
        date: now,
        time: now,
      });

      setMaxTimeStamp({
        date: maxDate,
        time: maxDate,
      });

      /**
       * Make sure selected value remains valid.
       */
      setTimeStamp((previous) => {
        const selectedDateTime = new Date(
          previous.date.getFullYear(),
          previous.date.getMonth(),
          previous.date.getDate(),
          previous.time.getHours(),
          previous.time.getMinutes(),
          0,
          0,
        );

        const validDateTime = clampDate(selectedDateTime, now, maxDate);

        return {
          date: validDateTime,
          time: validDateTime,
        };
      });

      return;
    }

    /**
     * -------------------------------------------------------
     * START TIME
     * -------------------------------------------------------
     */

    const startDate = toPickerDate(startTime);

    if (!startDate) {
      return;
    }

    const startMoment = pickerDateToMoment(startDate);

    /**
     * -------------------------------------------------------
     * MINIMUM DURATION
     *
     * start + minimum duration
     * -------------------------------------------------------
     */

    const effectiveMinimumMinutes =
      applyMinimumDuration &&
      ["Single Entry Reservation", "Réservation hockey"].includes(ticketType)
        ? minimumDurationMinutes
        : 0;

    const minMoment = startMoment
      .clone()
      .add(effectiveMinimumMinutes, "minutes");

    /**
     * IMPORTANT:
     * Apply the user's offset correction.
     */
    const minDate = convertToPickerDate(minMoment.toDate());

    /**
     * -------------------------------------------------------
     * MAXIMUM DURATION
     *
     * start + maximum duration
     * -------------------------------------------------------
     */

    const effectiveMaximumMinutes =
      applyMaximumDuration &&
      ["Single Entry Reservation", "Réservation hockey"].includes(ticketType)
        ? configuredMaximumDurationMinutes
        : maxMinutes;

    const maxMoment = startMoment
      .clone()
      .add(effectiveMaximumMinutes, "minutes");

    /**
     * IMPORTANT:
     * Apply the user's offset correction.
     */
    const maxDate = convertToPickerDate(maxMoment.toDate());

    /**
     * Update min constraint
     */
    setMinTimeStamp({
      date: minDate,
      time: minDate,
    });

    /**
     * Update max constraint
     */
    setMaxTimeStamp({
      date: maxDate,
      time: maxDate,
    });

    /**
     * -------------------------------------------------------
     * VALIDATE CURRENT VALUE
     * -------------------------------------------------------
     */

    setTimeStamp((previous) => {
      const selectedDateTime = new Date(
        previous.date.getFullYear(),
        previous.date.getMonth(),
        previous.date.getDate(),
        previous.time.getHours(),
        previous.time.getMinutes(),
        0,
        0,
      );

      const validDateTime = clampDate(selectedDateTime, minDate, maxDate);

      return {
        date: validDateTime,
        time: validDateTime,
      };
    });
  }, [
    startTime,
    applyMinimumDuration,
    minimumDurationMinutes,
    applyMaximumDuration,
    configuredMaximumDurationMinutes,
    maxMinutes,
  ]);

  /**
   * ---------------------------------------------------------
   * UPDATE END TIME
   * ---------------------------------------------------------
   */

  useEffect(() => {
    getEndTime(timeStamp.date, timeStamp.time, "endTime", rowIdx);

    // eslint-disable-next-line
  }, [timeStamp.date, timeStamp.time, rowIdx]);

  /**
   * ---------------------------------------------------------
   * VALIDATE SELECTED END TIME
   * ---------------------------------------------------------
   */

  useEffect(() => {
    const selectedDateTime = new Date(
      timeStamp.date.getFullYear(),
      timeStamp.date.getMonth(),
      timeStamp.date.getDate(),
      timeStamp.time.getHours(),
      timeStamp.time.getMinutes(),
      0,
      0,
    );

    const minDate = minTimeStamp.date;
    const maxDate = maxTimeStamp.date;

    /**
     * -------------------------------------------------------
     * BELOW MINIMUM
     * -------------------------------------------------------
     */

    if (selectedDateTime.getTime() < minDate.getTime()) {
      setTimeStamp({
        date: new Date(minDate),
        time: new Date(minDate),
      });

      return;
    }

    /**
     * -------------------------------------------------------
     * ABOVE MAXIMUM
     * -------------------------------------------------------
     */

    if (selectedDateTime.getTime() > maxDate.getTime()) {
      setTimeStamp({
        date: new Date(maxDate),
        time: new Date(maxDate),
      });

      return;
    }

    /**
     * -------------------------------------------------------
     * DETERMINE TIME PICKER LIMITS
     * -------------------------------------------------------
     */

    const selectedDay = moment(selectedDateTime).startOf("day");

    const minDay = moment(minDate).startOf("day");

    const maxDay = moment(maxDate).startOf("day");

    /**
     * Same day for minimum and maximum.
     */
    if (selectedDay.isSame(minDay) && selectedDay.isSame(maxDay)) {
      setShowFullTime(false);
      setShowEndOfDay(false);

      return;
    }

    /**
     * Selected date = minimum date.
     *
     * Example:
     *
     * start 10:00
     * min   10:30
     *
     * The time picker must start at 10:30.
     */
    if (selectedDay.isSame(minDay)) {
      setShowFullTime(false);
      setShowEndOfDay(true);

      return;
    }

    /**
     * Selected date = maximum date.
     *
     * Example:
     *
     * maximum = next day 15:00
     *
     * The time picker must stop at 15:00.
     */
    if (selectedDay.isSame(maxDay)) {
      setShowFullTime(true);
      setShowEndOfDay(false);

      return;
    }

    /**
     * Date is between minimum and maximum.
     *
     * Full day is allowed.
     */
    setShowFullTime(true);
    setShowEndOfDay(true);
  }, [timeStamp.date, timeStamp.time, minTimeStamp.date, maxTimeStamp.date]);

  /**
   * ---------------------------------------------------------
   * DATE CHANGE
   * ---------------------------------------------------------
   */

  const handleDateChange = (date: Date | null) => {
    if (!date) {
      return;
    }

    /**
     * Keep the selected time.
     */
    const currentTime = timeStamp.time;

    const newDateTime = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      currentTime.getHours(),
      currentTime.getMinutes(),
      0,
      0,
    );

    /**
     * Respect min/max duration.
     */
    const validDateTime = clampDate(
      newDateTime,
      minTimeStamp.date,
      maxTimeStamp.date,
    );

    setTimeStamp({
      date: validDateTime,
      time: validDateTime,
    });
  };

  /**
   * ---------------------------------------------------------
   * TIME CHANGE
   * ---------------------------------------------------------
   */

  const handleTimeChange = (time: Date | null) => {
    if (!time) {
      return;
    }

    const currentDate = timeStamp.date;

    const newDateTime = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
      time.getHours(),
      time.getMinutes(),
      0,
      0,
    );

    /**
     * Respect min/max duration.
     */
    const validDateTime = clampDate(
      newDateTime,
      minTimeStamp.date,
      maxTimeStamp.date,
    );

    setTimeStamp({
      date: validDateTime,
      time: validDateTime,
    });
  };

  /**
   * ---------------------------------------------------------
   * CURRENT SELECTED DAY
   * ---------------------------------------------------------
   */

  const selectedDay = moment(timeStamp.date).startOf("day");

  const minDay = moment(minTimeStamp.date).startOf("day");

  const maxDay = moment(maxTimeStamp.date).startOf("day");

  /**
   * ---------------------------------------------------------
   * TIME PICKER MINIMUM
   * ---------------------------------------------------------
   *
   * If selected date is the minimum date:
   *
   *     minTime = calculated minimum time
   *
   * Otherwise:
   *
   *     minTime = 00:00
   * ---------------------------------------------------------
   */

  const pickerMinTime = selectedDay.isSame(minDay)
    ? minTimeStamp.time
    : new Date(1970, 0, 1, 0, 0, 0, 0);

  /**
   * ---------------------------------------------------------
   * TIME PICKER MAXIMUM
   * ---------------------------------------------------------
   *
   * If selected date is the maximum date:
   *
   *     maxTime = calculated maximum time
   *
   * Otherwise:
   *
   *     maxTime = 23:59
   * ---------------------------------------------------------
   */

  const pickerMaxTime = selectedDay.isSame(maxDay)
    ? maxTimeStamp.time
    : new Date(1970, 0, 1, 23, 59, 0, 0);

  /**
   * ---------------------------------------------------------
   * END OF DAY
   * ---------------------------------------------------------
   */

  const endOfDayAsDate = new Date(1970, 0, 1, 23, 59, 0, 0);

  /**
   * ---------------------------------------------------------
   * RENDER
   * ---------------------------------------------------------
   */

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        width: "100%",
        justifyContent: "center",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          cursor: "pointer",
          p: 1,
        }}>
        <ClockOutline
          sx={{
            mr: 1,
            color: "rgb(0 0 0 / 49%)",
          }}
        />

        <div
          style={{
            display: "flex",
            flexDirection: "row",
          }}>
          {/* =================================================
              DATE PICKER
          ================================================= */}

          <DatePickerWrapper>
            <DatePicker
              disabled={isDisabled}
              showYearDropdown
              locale={currentLanguage}
              minDate={minTimeStamp.date}
              maxDate={maxTimeStamp.date}
              selected={timeStamp.date}
              onChange={handleDateChange}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={timeStamp.date}
                />
              }
            />
          </DatePickerWrapper>

          {/* =================================================
              TIME PICKER
          ================================================= */}

          <TimePickerWrapper
            sx={{
              marginLeft: -5,
            }}>
            <DatePicker
              disabled={isDisabled}
              locale={currentLanguage}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={5}
              dateFormat={currentLanguage === "en" ? "HH:mm" : "hh:mm"}
              timeFormat="HH:mm"
              selected={timeStamp.time}
              onChange={handleTimeChange}
              minTime={pickerMinTime}
              maxTime={pickerMaxTime}
              customInput={
                <CustomInputTime
                  label={t("Sélectionner une date")}
                  start={timeStamp.time}
                />
              }
            />
          </TimePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

// * subscriptions table date picker V1
// ! v2
// ? entry

export const PartnerSubscriptionDynamicEntryDatePicker: FC<any> = ({
  startDate,
  getStartDate,
  rowIdx,
  isDisabled,
  actionType,
}) => {
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  const [selectedDate, setSelectedDate] = useState<any>(() => {
    const currentDate = moment().utcOffset(0, true).startOf("day");

    if (!startDate) {
      return currentDate.toDate();
    }

    const initDate = moment.utc(startDate).startOf("day");
    const isDatePassed = initDate.isBefore(currentDate, "day");

    if (!isDatePassed || actionType === "UPDATE") {
      return initDate.toDate();
    } else {
      // Always return a date
      return currentDate.toDate();
    }
  });

  useEffect(() => {
    getStartDate(
      selectedDate,
      new Date().setHours(0, 0, 0, 0),
      "startTime",
      rowIdx,
    );
  }, [selectedDate, rowIdx]);

  useEffect(() => {
    const currentDate = moment().utcOffset(0, true).startOf("day");

    // ? NO INIT VALUE EXIST
    if (!startDate) {
      setSelectedDate(currentDate.toDate());
      return;
    }

    if (startDate && actionType) {
      const initDate = moment.utc(startDate).startOf("day");
      let targetDate = currentDate;

      const isDatePassed = initDate.isBefore(currentDate, "day");
      if (!isDatePassed || actionType === "UPDATE") {
        targetDate = initDate;
      }
      setSelectedDate(targetDate.toDate());
      return;
    }
  }, [startDate, actionType]);

  const handleDateChanges = (date: any) => {
    if (!date) return;
    setSelectedDate(date);
  };

  return (
    <Box
      sx={{
        flex: 1,
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "center",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
          justifyContent: "center",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <DatePickerWrapper>
            <DatePicker
              disabled={isDisabled}
              showYearDropdown
              locale={currentLanguage}
              minDate={new Date()}
              selected={selectedDate}
              onChange={handleDateChanges}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={selectedDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};

// ? exit
export const PartnerSubscriptionDynamicExitDatePicker: FC<any> = ({
  startDate,
  endDate,
  getEndDate,
  rowIdx,
  isDisabled,
  applyMinimumDuration,
  subscriptionMinimumDuration,
  applyMaximumDuration,
  subscriptionMaximumDuration,
}) => {
  // ** translation
  // ** translation
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  registerLocale(
    currentLanguage,
    currentLanguage === "fr" ? fr : currentLanguage === "nl" ? nl : en,
  );

  // * current date
  const currentDate = moment().utcOffset(0, true).startOf("day");

  // * current min unit & duration
  const minUnit = subscriptionMinimumDuration?.unit;
  const minDuration = subscriptionMinimumDuration?.value;

  // * current max unit & duration
  const maxUnit = subscriptionMaximumDuration?.unit;
  const maxDuration = subscriptionMaximumDuration?.value;

  const [selectedDate, setSelectedDate] = useState<any>(() => {
    let defaultDate: Date;
    if (applyMinimumDuration && subscriptionMinimumDuration) {
      defaultDate = currentDate
        .clone()
        .add(minDuration, minUnit)
        .startOf("day")
        .toDate();
    } else {
      defaultDate = currentDate.clone().add(1, "month").startOf("day").toDate();
    }
    if (!endDate) {
      return defaultDate;
    }
    return moment.utc(endDate).startOf("day").toDate();
  });

  // * default min & max dates if startDate dont exist
  let defaultMinDate: Date = new Date();
  let defaultMaxDate: Date = new Date();

  if (applyMinimumDuration && minUnit && minDuration) {
    defaultMinDate = currentDate.clone().add(minDuration, minUnit).toDate();
  } else {
    defaultMinDate = currentDate
      .clone()
      .add(1, "month")
      .startOf("day")
      .toDate();
  }

  if (applyMaximumDuration && maxUnit && maxDuration) {
    defaultMaxDate = currentDate.clone().add(maxDuration, maxUnit).toDate();
  } else {
    defaultMaxDate = currentDate
      .clone()
      .add(10, "years")
      .startOf("day")
      .toDate();
  }

  // * min & max dates if start date exist
  let selectedMinDate: Date = new Date();
  let selectedMaxDate: Date = new Date();

  if (startDate) {
    const start = moment.utc(startDate).startOf("day");
    if (applyMinimumDuration && minUnit && minDuration) {
      selectedMinDate = start.clone().add(minDuration, minUnit).toDate();
    } else {
      selectedMinDate = start.clone().add(1, "month").startOf("day").toDate();
    }

    if (applyMaximumDuration && maxUnit && maxDuration) {
      selectedMaxDate = start.clone().add(maxDuration, maxUnit).toDate();
    } else {
      selectedMaxDate = start.clone().add(10, "years").startOf("day").toDate();
    }
  }
  // Initialize constraints
  const [dateConstraints, setDateConstraints] = useState<{
    minDate: Date;
    maxDate: Date;
  }>(() => {
    if (!startDate) {
      return {
        minDate: defaultMinDate,
        maxDate: defaultMaxDate,
      };
    }

    try {
      return { minDate: selectedMinDate, maxDate: selectedMaxDate };
    } catch (error) {
      return {
        minDate: defaultMinDate,
        maxDate: defaultMaxDate,
      };
    }
  });

  // * UPDATE END DATE
  useEffect(() => {
    getEndDate(
      selectedDate,
      new Date().setHours(0, 0, 0, 0),
      "endTime",
      rowIdx,
    );
    // eslint-disable-next-line
  }, [selectedDate, rowIdx]);

  // * DATE CONSTRAINTS
  useEffect(() => {
    if (!startDate) {
      // Default constraints
      // const today = moment().utcOffset(0, true).startOf("day");
      // setDateConstraints({
      //   minDate: today.clone().add(1, "month").toDate(),
      //   maxDate: today.clone().add(10, "years").toDate(),
      // });
      setDateConstraints({
        minDate: defaultMinDate,
        maxDate: defaultMaxDate,
      });
      return;
    }

    // const start = moment.utc(startDate).startOf("day");
    // const minDate = start.clone().add(1, "month");
    // const maxDate = start.clone().add(10, "years");

    // setDateConstraints({
    //   minDate: minDate.toDate(),
    //   maxDate: maxDate.toDate(),
    // });
    setDateConstraints({
      minDate: selectedMinDate,
      maxDate: selectedMaxDate,
    });
  }, [startDate]);

  useEffect(() => {
    if (!endDate) {
      // No endDate, use default
      if (startDate) {
        // const defaultDate = moment
        //   .utc(startDate)
        //   .add(1, "month")
        //   .startOf("day")
        //   .toDate();
        setSelectedDate(selectedMinDate);
      } else {
        // const defaultDate = moment()
        //   .utcOffset(0, true)
        //   .add(1, "month")
        //   .startOf("day")
        //   .toDate();
        setSelectedDate(defaultMinDate);
      }
      return;
    }

    const end = moment.utc(endDate).startOf("day");
    let targetDate = end;

    const minDate = moment.utc(dateConstraints.minDate).startOf("day");
    const maxDate = moment.utc(dateConstraints.maxDate).startOf("day");

    const isAfterMax = end.isAfter(maxDate, "day");

    if (isAfterMax) {
      targetDate = maxDate;
    }

    const isBefore = end.isBefore(minDate, "day");
    if (isBefore) {
      targetDate = minDate;
    }

    if (!end.isSame(targetDate)) {
      setSelectedDate(targetDate.toDate());
    }
  }, [startDate, endDate, dateConstraints]);

  const handleDateChanges = (date: any) => {
    if (!date) return;
    setSelectedDate(date);
  };

  return (
    <Box
      sx={{
        flex: 1,
        borderRadius: "0.4rem",
        display: "flex",
        color: "#8A8A8A",
        justifyContent: "space-between",
        maxWidth: "100%",
      }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          p: 1,
          cursor: "pointer",
          justifyContent: "center",
        }}>
        <CalendarMonthOutline sx={{ mr: 1, color: "rgb(0 0 0 / 49%)" }} />
        <div>
          <DatePickerWrapper>
            <DatePicker
              disabled={isDisabled}
              showYearDropdown
              locale={currentLanguage}
              minDate={dateConstraints.minDate}
              maxDate={dateConstraints.maxDate}
              selected={selectedDate}
              onChange={handleDateChanges}
              customInput={
                <CustomDateInput
                  label={t("Sélectionner une date")}
                  start={selectedDate}
                />
              }
            />
          </DatePickerWrapper>
        </div>
      </Box>
    </Box>
  );
};
